import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { MoreVertical, Trash2, Share2, Loader2, SquarePen, Files, ChevronRight, Dot, Sparkles, CalendarDays } from "lucide-react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Sidebar } from "@/components/Sidebar";
import { ModelSelector } from "@/components/ModelSelector";
import { AuthModal } from "@/components/AuthModal";
import { AdModal } from "@/components/AdModal";
import { SEOHead } from "@/components/SEOHead";
import { ChatInterface } from "@/components/ChatInterface";
import { WelcomeScreen } from "@/components/WelcomeScreen";
import { BackgroundWaves } from "@/components/BackgroundVideo";
import SelectionActionsTooltip from "@/components/SelectionActionsTooltip";
import { ChatInputArea } from "@/components/ChatInputArea";
import { ScrollToBottomButton } from "@/components/ScrollToBottomButton";
import { IndexSkeletonLoader } from "@/components/IndexSkeletonLoader";
import { ChatHistoryModal } from "@/components/ChatHistoryModal";
import { ImageCarousel } from "@/components/search/ImageCarousel";
import { SourcesDrawer } from "@/components/search/SourcesDrawer";
import { useToast } from "@/hooks/use-toast";
import { LimitCard } from "../components/LimitCard";
import { PricingModal } from "@/components/PricingModal";
import { getRandomSuggestions } from "@/data/chatSuggestions";
import { storage, StorageManager } from "@/utils/storageManager";
import { useMessageHandler } from "@/components/MessageHandler";
import { useRegenerateHandler } from "@/components/RegenerateHandler";
import { AttachedMedia, AIAgent } from "@/types/aiAgents";
import { useSupabase } from "@/hooks/useSupabase";
import { LOCAL_STORAGE_KEYS } from "@/utils/storageKeys";
import { useChatStorage } from "@/hooks/useChatStorage";
import { useLazyMessages } from "@/hooks/useLazyMessages";
import { FeedbackModal } from "@/components/FeedbackModal";
import { TempChatHeader } from "@/components/TempChatHeader";
import { useTempChat } from "@/contexts/TempChatContext";
import { useTheme } from "@/contexts/ThemeContext";
import { IncognitoModeModal } from "@/components/IncognitoModeModal";
import ChatRedirect from "@/components/ChatRedirect";
import { cn } from "@/lib/utils";
import SidepanelIcon from "../../packages/svgs/SidepanelIcon";
import { ShareDialog } from "@/components/ShareDialog";
import LookAroundIcon from "../../packages/svgs/LookAroundIcon";
import { supabase } from "@/integrations/supabase/client";
import { EditIcon } from "../../packages/svgs";
import { getCloudinaryPreviewUrl } from '@/utils/cloudinary';
import { getDailyQuotaStatus, consumeDailyQuota } from '@/services/dailyQuotaService';
// import OreogSvgLoader from '@/components/OreogSvgLoader';
import { getMemeTemplates, formatMemeTemplates } from "@/services/agents/memeAgent";

interface ChatData {
  id: string;
  title: string;
  messages: any[];
  lastMessage: string;
  messageCount: number;
  model: string;
  date: string;
  timestamp: Date;
}

interface ImageModalData {
  images: { url: string; description?: string }[];
  initialIndex: number;
}

// Get the storage manager instance for direct method access
const storageManager = StorageManager.getInstance();
const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { chatId: routeChatId } = useParams();

  // Use Supabase hook for authentication
  const { user, session, loading: authLoading, signOut } = useSupabase();

  // Use temporary chat context
  const {
    isTempMode,
    setTempMode,
    tempMessages,
    addTempMessage,
    clearTempMessages,
    showIncognitoModal,
    setShowIncognitoModal,
    startIncognitoSession,
  } = useTempChat();
  const { theme, setTheme } = useTheme();

  // Use Supabase chat storage when authenticated, fallback to localStorage
  const supabaseChatStorage = useChatStorage(user);

  // UI State - Set to false by default
  const [isOpen, setIsOpen] = useState(false);
  // Incognito reload confirmation modal
  const [showIncognitoReloadConfirm, setShowIncognitoReloadConfirm] = useState(false);
  const [isReloadingIncognito, setIsReloadingIncognito] = useState(false);

  // Keep URL in sync with incognito mode
  useEffect(() => {
    const onIncognito = location.pathname === '/incognito';
    if (isTempMode && !onIncognito) {
      navigate('/incognito', { replace: true });
    } else if (!isTempMode && onIncognito) {
      navigate('/', { replace: true });
    }
  }, [isTempMode, location.pathname, navigate]);

  // Prevent refresh in incognito and show custom confirm modal
  useEffect(() => {
    const isOnIncognito = location.pathname === '/incognito';
    if (!isTempMode || !isOnIncognito) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key?.toLowerCase();
      const isRefreshShortcut = key === 'f5' || ((e.ctrlKey || e.metaKey) && key === 'r');
      if (isRefreshShortcut) {
        e.preventDefault();
        e.stopPropagation();
        setShowIncognitoReloadConfirm(true);
      }
    };

    // As a safety net for non-keyboard refresh (toolbar/address bar), show native confirm
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true } as any);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isTempMode, location.pathname]);

  const confirmIncognitoReload = () => {
    setIsReloadingIncognito(true);
    try {
      clearTempMessages();
    } catch { }
    // Perform hard reload
    window.location.reload();
  };
  const [isBranchingTransition, setIsBranchingTransition] = useState(false);
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  const isDarkMode =
    theme === "dark" || (theme === "system" && systemPrefersDark);

  // Sources Drawer State
  const [showSourcesDrawer, setShowSourcesDrawer] = useState(false);
  const [sourcesDrawerData, setSourcesDrawerData] = useState<{
    sources: any[];
    searchDepth: "basic" | "advanced";
    responseTime: number;
    isDarkMode: boolean;
  } | null>(null);

  // Full-screen Image Modal State
  const [showFullscreenImageModal, setShowFullscreenImageModal] =
    useState(false);
  const [fullscreenImageData, setFullscreenImageData] =
    useState<ImageModalData | null>(null);
  // Files in this chat modal
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [filesModalPurpose, setFilesModalPurpose] = useState<'view' | 'pick'>('view');
  const [librarySelectedUrls, setLibrarySelectedUrls] = useState<string[]>([]);

  const inputBarRef = useRef<HTMLDivElement | null>(null);
  const [inputBarHeight, setInputBarHeight] = useState<number>(0);


  const [isBelowSm, setIsBelowSm] = useState(false);

  // Header visibility state
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isVoiceModeActive, setIsVoiceModeActive] = useState(false);
  const [isWelcomeInputExpanded, setIsWelcomeInputExpanded] = useState(false);
  const [backgroundType, setBackgroundType] = useState<'solid' | 'image'>('solid');

  /*
  const [backgroundType, setBackgroundType] = useState<'solid' | 'image'>(() => {
    try {
      // @ts-ignore
      return storage.getAppSettings()?.backgroundType === 'image' ? 'image' : 'solid';
    } catch {
      return 'solid';
    }
  });
  
  useEffect(() => {
    const handleAppSettingsChange = (e: CustomEvent) => {
      const nextType = e.detail?.backgroundType;
      if (nextType === 'solid' || nextType === 'image') {
        setBackgroundType(nextType);
      }
    };
    window.addEventListener('appSettingsChanged', handleAppSettingsChange as EventListener);
    return () => {
      window.removeEventListener('appSettingsChanged', handleAppSettingsChange as EventListener);
    };
  }, []);
  */

  // Initialize credits and daily refresh credits
  useEffect(() => {
    const safeParseInt = (v: string | null, fallback: number) => {
      const n = parseInt(String(v ?? ''), 10);
      return Number.isFinite(n) ? n : fallback;
    };

    const getNextDailyReset = () => {
      const now = new Date();
      const reset = new Date(now);
      reset.setHours(0, 30, 0, 0);
      if (now.getTime() >= reset.getTime()) {
        reset.setDate(reset.getDate() + 1);
      }
      return reset;
    };

    const load = async () => {
      try {
        if (user) {
          setIsCreditsLoading(true);
          const status = await getDailyQuotaStatus();
          setCreditsRemaining(status?.remaining?.messages ?? 0);
          setDailyCreditsRemaining(status?.limits?.messages ?? 0);
          setCreditsTier((status?.tier || 'free').toString());
          setCreditsResetDate(status?.reset_date);
          setIsCreditsLoading(false);
          return;
        }

        const storedCredits =
          localStorage.getItem(LOCAL_STORAGE_KEYS.creditsRemaining) ||
          localStorage.getItem('creditsRemaining');
        const nextCredits = storedCredits == null ? 200 : safeParseInt(storedCredits, 200);
        if (storedCredits == null) {
          localStorage.setItem(LOCAL_STORAGE_KEYS.creditsRemaining, String(nextCredits));
        }
        setCreditsRemaining(nextCredits);

        const resetAtRaw =
          localStorage.getItem(LOCAL_STORAGE_KEYS.dailyCreditsResetAt) ||
          localStorage.getItem('dailyCreditsResetAt');
        const resetAtMs = safeParseInt(resetAtRaw, 0);
        const nowMs = Date.now();
        const shouldReset = !resetAtMs || nowMs >= resetAtMs;
        if (shouldReset) {
          const nextReset = getNextDailyReset();
          localStorage.setItem(LOCAL_STORAGE_KEYS.dailyCreditsRemaining, '300');
          localStorage.setItem(LOCAL_STORAGE_KEYS.dailyCreditsResetAt, String(nextReset.getTime()));
          setDailyCreditsRemaining(300);
        } else {
          const storedDaily =
            localStorage.getItem(LOCAL_STORAGE_KEYS.dailyCreditsRemaining) ||
            localStorage.getItem('dailyCreditsRemaining');
          const nextDaily = storedDaily == null ? 300 : safeParseInt(storedDaily, 300);
          if (storedDaily == null) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.dailyCreditsRemaining, String(nextDaily));
          }
          setDailyCreditsRemaining(nextDaily);
        }
      } catch {
        setCreditsRemaining(200);
        setDailyCreditsRemaining(300);
        setCreditsTier('free');
        setCreditsResetDate(undefined);
        setIsCreditsLoading(false);
      }
    };

    void load();
  }, [user]);

  const refreshCredits = useCallback(async () => {
    if (!user) return;
    try {
      setIsCreditsLoading(true);
      const status = await getDailyQuotaStatus();
      setCreditsRemaining(status?.remaining?.messages ?? 0);
      setDailyCreditsRemaining(status?.limits?.messages ?? 0);
      setCreditsTier((status?.tier || 'free').toString());
      setCreditsResetDate(status?.reset_date);
    } catch (e) {
      console.error('Failed to refresh credits:', e);
    } finally {
      setIsCreditsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!showCreditsMenu) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (creditsMenuRef.current && creditsMenuRef.current.contains(target)) return;
      setShowCreditsMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown as any);
    };
  });
  const [lastScrollY, setLastScrollY] = useState(0);

  // Modal State
  const [showAuth, setShowAuth] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showWelcomePricing, setShowWelcomePricing] = useState(false);
  const [pricingLimitMessage, setPricingLimitMessage] = useState<string | undefined>(undefined);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackInitialMessage, setFeedbackInitialMessage] = useState("");
  const [feedbackTriggerSource, setFeedbackTriggerSource] = useState("Settings");
  const [modalImages, setModalImages] = useState<string[]>([]);
  const [modalInitialIndex, setModalInitialIndex] = useState(0);

  // Chat State
  const [messages, setMessages] = useState([]);
  const [allMessages, setAllMessages] = useState([]); // All messages for lazy loading
  const [inputValue, setInputValue] = useState("");
  const [selectedModel, setSelectedModel] = useState("chat gpt");
  const [isImageMode, setIsImageMode] = useState(false);
  const [isWebSearchMode, setIsWebSearchMode] = useState(false);
  const [isDeepResearchMode, setIsDeepResearchMode] = useState(false);
  const [isQuizMode, setIsQuizMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAttachmentUploadingGlobal, setIsAttachmentUploadingGlobal] = useState(false);

  // Multi-AI selection state
  const [isMultiAIMode, setIsMultiAIMode] = useState(false);
  const [selectedMultiAIModels, setSelectedMultiAIModels] = useState<string[]>([]);
  const [filesCollapsed, setFilesCollapsed] = useState<{ ai: boolean; attached: boolean; docs: boolean; web: boolean }>({ ai: false, attached: false, docs: false, web: true });

  useEffect(() => {
    const onStart = () => setIsAttachmentUploadingGlobal(true);
    const onDone = () => setIsAttachmentUploadingGlobal(false);
    try {
      window.addEventListener('attachmentUpload:start', onStart as EventListener);
      window.addEventListener('attachmentUpload:done', onDone as EventListener);
    } catch { }

    return () => {
      try {
        window.removeEventListener('attachmentUpload:start', onStart as EventListener);
        window.removeEventListener('attachmentUpload:done', onDone as EventListener);
      } catch { }
    };
  }, []);

  useEffect(() => {
    const handleShowFeedback = (e: any) => {
      if (e.detail?.message) {
        setFeedbackInitialMessage(e.detail.message);
      } else {
        setFeedbackInitialMessage("");
      }

      if (e.detail?.isDislikeTrigger) {
        setFeedbackTriggerSource("Chat Dislike");
      } else {
        setFeedbackTriggerSource("Settings");
      }

      setShowFeedbackForm(true);
    };

    window.addEventListener('showFeedbackForm', handleShowFeedback as EventListener);
    return () => {
      window.removeEventListener('showFeedbackForm', handleShowFeedback as EventListener);
    };
  }, []);

  useEffect(() => {
    const el = inputBarRef.current;
    if (!el) return;

    const update = () => {
      try {
        const rect = el.getBoundingClientRect();
        setInputBarHeight(Math.max(0, Math.round(rect.height)));
      } catch { }
    };

    update();

    let ro: ResizeObserver | null = null;
    try {
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => update());
        ro.observe(el);
      }
    } catch { }

    window.addEventListener('resize', update, { passive: true } as any);
    return () => {
      window.removeEventListener('resize', update as any);
      try { ro?.disconnect(); } catch { }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 639px)');
    const update = () => setIsBelowSm(!!mq.matches);
    update();
    try {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    } catch {
      try {
        mq.addListener(update);
        return () => mq.removeListener(update);
      } catch {
        return;
      }
    }
  }, []);

  // Helper to extract images for the "Files in this chat" modal (robust)
  const extractImagesFromMessages = useCallback(() => {
    const out: { url: string; source: 'created' | 'added'; category: 'ai' | 'attached' | 'web' | 'agents' }[] = [];
    const isAssistant = (m: any) => m.type === 'assistant' || m.role === 'assistant' || m.type === 'ai';

    const isAgentDomain = (u: string) => /(?:images\.unsplash\.com|unsplash\.com|images\.pexels\.com|pexels\.com|images\.pixabay\.com|pixabay\.com|upload\.wikimedia\.org|wikipedia\.org)/i.test(u);
    const isWebSearchDomain = (u: string) => /(?:gstatic\.com|googleusercontent\.com|bing\.net|duckduckgo\.com|yandex\.net|wp\.com)/i.test(u);

    // Modified to handle both local and remote image URLs
    const pushIf = (url?: string, source: 'created' | 'added' = 'added') => {
      if (!url || typeof url !== 'string') return false;
      // Check for data URLs or common image extensions
      const isImage = url.startsWith('data:image') ||
        /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(url) ||
        isAgentDomain(url) || isWebSearchDomain(url);

      if (isImage) {
        if (!out.some(img => img.url === url)) {
          const category: 'ai' | 'attached' | 'web' | 'agents' =
            source === 'added' ? 'attached' : (isAgentDomain(url) ? 'agents' : (isWebSearchDomain(url) ? 'web' : 'ai'));
          out.push({ url, source, category });
          return true;
        }
      }
      return false;
    };

    (messages as any[]).forEach((m: any) => {
      const source: 'created' | 'added' = isAssistant(m) ? 'created' : 'added';

      // attachments arrays commonly used in our UI
      const arrs: any[] = [m.attachments, m.media, m.images].filter(Boolean);
      arrs.forEach((arr) => {
        (arr as any[]).forEach((item) => {
          const url = item?.url || item?.src || item?.image_url || item?.image || item;
          pushIf(url, source);
        });
      });

      // single attachedMedia object (legacy)
      if (m.attachedMedia) {
        const url = m.attachedMedia.url || m.attachedMedia.src;
        pushIf(url, source);
      }

      // markdown images ![alt](url) and HTML <img src="...">
      const content = String(m.content || '');
      const mdImg = /!\[[^\]]*\]\(([^)]+)\)/g; let md;
      while ((md = mdImg.exec(content)) !== null) pushIf(md[1], source);
      const htmlImg = /<img[^>]*src=["']([^"']+)["'][^>]*>/gi; let hi;
      while ((hi = htmlImg.exec(content)) !== null) pushIf(hi[1], source);

      // plain image URLs
      const urlRegex = /(https?:\/\/[\w\-\.\/%#?=&:+~]+\.(?:png|jpe?g|gif|webp|bmp|svg)(?:\?[\w\-\.\/%#?=&:+~]*)?)/gi; let match;
      while ((match = urlRegex.exec(content)) !== null) pushIf(match[1], source);

      // some agents return blocks like {type:'image',url:'...'} in content JSON
      try {
        if (content.trim().startsWith('{') || content.trim().startsWith('[')) {
          const parsed = JSON.parse(content);
          const scan = (node: any) => {
            if (!node) return;
            if (Array.isArray(node)) return node.forEach(scan);
            if (typeof node === 'object') {
              pushIf(node.url, source); pushIf(node.src, source); pushIf(node.image_url, source);
              Object.values(node).forEach(scan);
            }
          };
          scan(parsed);
        }
      } catch { }
    });

    // de-dup
    const seen = new Set<string>();
    return out.filter((img) => { if (seen.has(img.url)) return false; seen.add(img.url); return true; });
  }, [messages]);

  const hasLibraryImages = useMemo(() => {
    try {
      const imgs = extractImagesFromMessages();
      return Array.isArray(imgs) && imgs.length > 0;
    } catch {
      return false;
    }
  }, [extractImagesFromMessages]);

  const toggleLibrarySelectedUrl = useCallback((url: string) => {
    if (!url) return;
    setLibrarySelectedUrls((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const exists = current.includes(url);
      if (exists) return current.filter((u) => u !== url);
      return [...current, url];
    });
  }, []);

  const confirmAttachLibrarySelected = useCallback(() => {
    if (!librarySelectedUrls || librarySelectedUrls.length === 0) {
      setShowFilesModal(false);
      setFilesModalPurpose('view');
      return;
    }
    setAttachments((prev) => {
      const next = Array.isArray(prev) ? prev.slice() : [];
      const remainingSlots = Math.max(0, 7 - next.length);
      const toAdd = librarySelectedUrls.slice(0, remainingSlots);
      for (const url of toAdd) {
        if (!next.some((m: any) => m?.type === 'image' && m?.url === url)) {
          next.push({ type: 'image', url, name: 'Library image' } as any);
        }
      }
      return next;
    });
    setLibrarySelectedUrls([]);
    setShowFilesModal(false);
    setFilesModalPurpose('view');
  }, [librarySelectedUrls]);

  const extractDocsFromMessages = useCallback(() => {
    const out: Array<{ name: string; originalExt?: string; pageUrls: string[]; source: 'created' | 'added' }> = [];
    const isAssistant = (m: any) => m.type === 'assistant' || m.role === 'assistant' || m.type === 'ai';

    const pushDoc = (doc: any, source: 'created' | 'added') => {
      if (!doc) return;
      const name = String(doc?.name || 'Document');
      const originalExt = doc?.originalExt ? String(doc.originalExt) : undefined;
      const pageUrlsRaw: any[] = (doc?.convertedImageUrls || doc?.pageUrls || doc?.pages || []).filter(Boolean);
      const pageUrls = pageUrlsRaw
        .map((u) => (typeof u === 'string' ? u : (u?.url || u?.src)))
        .filter((u) => typeof u === 'string' && u.length > 0) as string[];
      if (pageUrls.length === 0) return;

      const key = `${name}::${pageUrls[0]}`;
      if (out.some((d) => `${d.name}::${d.pageUrls[0]}` === key)) return;
      out.push({ name, originalExt, pageUrls, source });
    };

    (messages as any[]).forEach((m: any) => {
      const source: 'created' | 'added' = isAssistant(m) ? 'created' : 'added';
      const ad = (m as any)?.agentData;
      if (ad?.attachedMedia && ad.attachedMedia.type === 'document') {
        pushDoc(ad.attachedMedia, source);
      }
      if ((m as any)?.attachedMedia && (m as any).attachedMedia.type === 'document') {
        pushDoc((m as any).attachedMedia, source);
      }
      // Only treat attachedMediaList as document pages when the message is a document.
      // For image multi-attachments, attachedMediaList contains images and must NOT be shown as docs.
      const isDocMessage =
        ((m as any)?.attachedMedia && (m as any).attachedMedia.type === 'document') ||
        (ad?.attachedMedia && ad.attachedMedia.type === 'document');
      if (isDocMessage && Array.isArray((m as any)?.attachedMediaList) && (m as any).attachedMediaList.length > 0) {
        const list = (m as any).attachedMediaList;
        pushDoc({ name: (m as any)?.attachedMedia?.name || ad?.attachedMedia?.name || 'Document', originalExt: (m as any)?.attachedMedia?.originalExt || ad?.attachedMedia?.originalExt, convertedImageUrls: list.map((x: any) => x?.url).filter(Boolean) }, source);
      }
    });

    return out;
  }, [messages]);

  // Lazy loading for messages
  const {
    displayedMessages,
    hasMore,
    isLoadingMore,
    loadMore,
    reset: resetLazyLoading,
  } = useLazyMessages(allMessages, {
    initialBatchSize: 20,
    batchSize: 15,
  });

  // Use displayed messages for rendering
  useEffect(() => {
    setMessages(displayedMessages);
  }, [displayedMessages]);

  // Create a wrapper function that updates both messages and allMessages
  // This is used by MessageHandler to add new messages
  const setMessagesAndSync = useCallback((updater: any) => {
    setMessages((prev) => {
      const newMessages = typeof updater === 'function' ? updater(prev) : updater;
      // Also update allMessages to include new messages
      setAllMessages(newMessages);
      return newMessages;
    });
  }, []);
  const [loadingContext, setLoadingContext] = useState<{
    context:
    | "image"
    | "chat-agent"
    | "chat-model"
    | "web-search"
    | "deep-research"
    | "quiz";
    name: string;
    userQuery?: string;
  } | null>(null);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [currentChatTitle, setCurrentChatTitle] = useState<string>("");
  const [suggestions, setSuggestions] = useState([]);

  // Chat Menu State
  const [showChatMenu, setShowChatMenu] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [newChatName, setNewChatName] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const chatMenuRef = useRef<HTMLDivElement>(null);

  // Agent State
  const [selectedAgent, setSelectedAgent] = useState<AIAgent | null>(null);
  // New multi-attachment state for chat input (Stage 1)
  const [attachments, setAttachments] = useState<AttachedMedia[]>([]);
  const [precomputedFallbackCollage, setPrecomputedFallbackCollage] = useState<{ file?: File; dataUrl?: string } | null>(null);
  // Backwards-compatible alias: first attachment as single attachedMedia
  const attachedMedia: AttachedMedia | null =
    attachments.length > 0 ? attachments[0] : null;
  const setAttachedMedia = (media: AttachedMedia | null) => {
    setAttachments(media ? [media] : []);
  };
  const [memeTemplates, setMemeTemplates] = useState<any[]>([]);
  const [selectedMemeTemplate, setSelectedMemeTemplate] = useState<any>(null);
  const [showMemeTemplates, setShowMemeTemplates] = useState(false);
  const [formattedMemeTemplates, setFormattedMemeTemplates] = useState<string | null>(null);
  const [gifs, setGifs] = useState<any[]>([]);
  const [showGifs, setShowGifs] = useState(false);
  // Start hydrating as true to prevent early save/navigation before URL load resolves
  const isHydratingRef = useRef(true);
  const savedMessageIdsRef = useRef<Set<string | number>>(new Set());
  const pendingUserQueueRef = useRef<any[]>([]);
  const autoSendRef = useRef<{ shouldSend: boolean; message: string; mode: string; media: any }>({ shouldSend: false, message: '', mode: 'chat', media: null });
  const lastBackgroundToggleRef = useRef<number>(0);
  const currentSessionIdRef = useRef<string | null>(null);
  const isCreatingSessionRef = useRef<boolean>(false);
  const leftEdgeDragStateRef = useRef<{
    dragging: boolean;
    pointerId: number | null;
    startX: number;
    startY: number;
    usingTouch: boolean;
    originalUserSelect: string | null;
    originalTouchAction: string | null;
    timeoutId?: number | null;
  }>({
    dragging: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    usingTouch: false,
    originalUserSelect: null,
    originalTouchAction: null,
  });

  // Auto-show meme templates when meme agent is selected
  useEffect(() => {
    const autoShowMemeTemplates = async () => {
      if (selectedAgent?.id === "meme" || selectedAgent?.id === "memes") {
        try {
          const templates = await getMemeTemplates();
          setMemeTemplates(templates);
          setShowMemeTemplates(true);
          // Preload formatted templates
          setFormattedMemeTemplates(formatMemeTemplates(templates));

          // Add a message showing the meme templates
          const memeTemplateMessage = {
            id: Date.now(),
            type: "ai" as const,
            content: formatMemeTemplates(templates),
            timestamp: new Date(),
            model: "meme-agent",
            selectedAgent: "meme",
          };

          setMessagesAndSync((prev) => [...prev, memeTemplateMessage]);
        } catch (error) {
          console.error("Error loading meme templates:", error);
        }
      }
    };

    if (selectedAgent?.id === "meme" || selectedAgent?.id === "memes") {
      autoShowMemeTemplates();
    }
  }, [selectedAgent]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        if (isImageMode) {
          setPrecomputedFallbackCollage(null);
          return;
        }

        const imageList = (attachments || []).filter((a) => a?.type === 'image' && (a.file || a.url)).slice(0, 7);
        if (imageList.length <= 5) {
          setPrecomputedFallbackCollage(null);
          return;
        }

        const rest = imageList.slice(4, 7);
        const files: File[] = [];

        for (let i = 0; i < rest.length; i++) {
          const m = rest[i] as any;
          if (m?.file) {
            files.push(m.file);
          } else if (m?.url) {
            const res = await fetch(m.url);
            const blob = await res.blob();
            const type = blob.type || 'image/png';
            files.push(new File([blob], `fallback_${i + 1}.png`, { type }));
          }
        }

        if (files.length === 0) {
          setPrecomputedFallbackCollage(null);
          return;
        }

        const createLabeledCollageFromImages = async (collageFiles: File[], labelText: string) => {
          const imgs: HTMLImageElement[] = [];
          for (const f of collageFiles) {
            const dataUrl: string = await new Promise((resolve, reject) => {
              const fr = new FileReader();
              fr.onload = () => resolve(fr.result as string);
              fr.onerror = reject;
              fr.readAsDataURL(f);
            });
            const img = await new Promise<HTMLImageElement>((resolve, reject) => {
              const el = new Image();
              el.onload = () => resolve(el);
              el.onerror = reject;
              el.src = dataUrl;
            });
            imgs.push(img);
          }

          const n = Math.min(3, imgs.length);
          const gap = 16;
          const radius = 20;
          const maxCanvas = 1600;
          const cell = Math.min(Math.floor((maxCanvas - 3 * gap) / 2), 720);

          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null;
          if (!ctx) throw new Error('Canvas unavailable');

          canvas.width = 2 * cell + 3 * gap;
          canvas.height = 2 * cell + 3 * gap;

          const frames = [
            { x: gap, y: gap, w: cell, h: cell },
            { x: 2 * gap + cell, y: gap, w: cell, h: cell },
            { x: gap, y: 2 * gap + cell, w: cell, h: cell },
            { x: 2 * gap + cell, y: 2 * gap + cell, w: cell, h: cell },
          ];

          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          const roundRectPath = (x: number, y: number, w: number, h: number, r: number) => {
            const rr = Math.min(r, w / 2, h / 2);
            const p = new Path2D();
            p.moveTo(x + rr, y);
            p.lineTo(x + w - rr, y);
            p.quadraticCurveTo(x + w, y, x + w, y + rr);
            p.lineTo(x + w, y + h - rr);
            p.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
            p.lineTo(x + rr, y + h);
            p.quadraticCurveTo(x, y + h, x, y + h - rr);
            p.lineTo(x, y + rr);
            p.quadraticCurveTo(x, y, x + rr, y);
            p.closePath();
            return p;
          };

          const drawContain = (image: HTMLImageElement, fx: number, fy: number, fw: number, fh: number) => {
            const iw = image.width;
            const ih = image.height;
            const scale = Math.min(fw / iw, fh / ih);
            const dw = Math.max(1, Math.floor(iw * scale));
            const dh = Math.max(1, Math.floor(ih * scale));
            const dx = Math.floor(fx + (fw - dw) / 2);
            const dy = Math.floor(fy + (fh - dh) / 2);
            const path = roundRectPath(fx, fy, fw, fh, radius);
            ctx.save();
            ctx.clip(path);
            ctx.drawImage(image, dx, dy, dw, dh);
            ctx.restore();
            ctx.strokeStyle = '#e5e7eb';
            ctx.lineWidth = 4;
            ctx.stroke(path);
          };

          for (let i = 0; i < n; i++) {
            const f = frames[i];
            drawContain(imgs[i], f.x, f.y, f.w, f.h);
          }

          const tf = frames[3];
          const textPath = roundRectPath(tf.x, tf.y, tf.w, tf.h, radius);
          ctx.save();
          ctx.clip(textPath);
          ctx.fillStyle = '#f9fafb';
          ctx.fillRect(tf.x, tf.y, tf.w, tf.h);
          ctx.restore();
          ctx.strokeStyle = '#e5e7eb';
          ctx.lineWidth = 4;
          ctx.stroke(textPath);

          ctx.fillStyle = '#111827';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          const fontSize = Math.max(18, Math.min(34, Math.floor(tf.w / 18)));
          ctx.font = `600 ${fontSize}px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial`;

          const lines = String(labelText || '').split(/\n+/g).map((s) => s.trim()).filter(Boolean);
          const safeLines = lines.length ? lines.slice(0, 4) : ["It's a collage of 3 different separate images"];
          const lineHeight = Math.round(fontSize * 1.35);
          const totalH = safeLines.length * lineHeight;
          let y = tf.y + tf.h / 2 - totalH / 2 + lineHeight / 2;
          for (const line of safeLines) {
            ctx.fillText(line, tf.x + tf.w / 2, y);
            y += lineHeight;
          }

          const blob: Blob = await new Promise((resolve) => canvas.toBlob((b) => resolve(b!), 'image/png'));
          const file = new File([blob], 'collage.png', { type: 'image/png' });
          const dataUrlInline: string = await new Promise((resolve) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result as string);
            r.readAsDataURL(file);
          });
          return { dataUrl: dataUrlInline, file };
        };

        const collage = await createLabeledCollageFromImages(files.slice(0, 3), "It's a collage of 3 diffrent seprate image");
        if (cancelled) return;
        setPrecomputedFallbackCollage({ file: collage.file, dataUrl: collage.dataUrl });
      } catch {
        if (!cancelled) setPrecomputedFallbackCollage(null);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [attachments, isImageMode]);

  // Handle navigation from Discover page with initial message and mode
  useEffect(() => {
    const state = location.state as any;
    if (state?.fromDiscover && state?.initialMessage) {
      // Set the input value and mode from Discover page
      setInputValue(state.initialMessage);

      // Set the chat mode based on what was selected in Discover
      if (state.initialMode === 'image') {
        setIsImageMode(true);
        setIsWebSearchMode(false);
        setIsDeepResearchMode(false);
        setIsQuizMode(false);
      } else if (state.initialMode === 'web') {
        setIsImageMode(false);
        setIsWebSearchMode(true);
        setIsDeepResearchMode(false);
        setIsQuizMode(false);
      } else if (state.initialMode === 'deep-research') {
        setIsImageMode(false);
        setIsWebSearchMode(false);
        setIsDeepResearchMode(true);
        setIsQuizMode(false);
      } else if (state.initialMode === 'quiz') {
        setIsImageMode(false);
        setIsWebSearchMode(false);
        setIsDeepResearchMode(false);
        setIsQuizMode(true);
      } else {
        // Default to chat mode
        setIsImageMode(false);
        setIsWebSearchMode(false);
        setIsDeepResearchMode(false);
        setIsQuizMode(false);
      }

      // Set attached media if provided (normalize to attachments array)
      if (state.initialMedia) {
        setAttachments([state.initialMedia]);
      }

      // Set selected agent if provided
      if (state.initialAgent) {
        setSelectedAgent(state.initialAgent);
      }

      // Store the data in ref for auto-send AFTER setting all state
      autoSendRef.current = {
        shouldSend: true,
        message: state.initialMessage,
        mode: state.initialMode || 'chat',
        media: state.initialMedia || null,
      };

      // Clear the location state to prevent re-triggering
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location]);

  // Auto-send message when coming from Discover page - wait for all state to be ready
  useEffect(() => {
    if (!autoSendRef.current.shouldSend) return;

    // Check if all required state is properly set
    const isStateReady =
      inputValue === autoSendRef.current.message &&
      (
        (autoSendRef.current.mode === 'image' && isImageMode) ||
        (autoSendRef.current.mode === 'web' && isWebSearchMode) ||
        (autoSendRef.current.mode === 'deep-research' && isDeepResearchMode) ||
        (autoSendRef.current.mode === 'quiz' && isQuizMode) ||
        (autoSendRef.current.mode === 'chat' && !isImageMode && !isWebSearchMode && !isDeepResearchMode && !isQuizMode)
      );

    if (isStateReady) {
      autoSendRef.current.shouldSend = false;
      // Trigger send on next tick to ensure all state is updated
      setTimeout(() => {
        handleSendClick();
      }, 100);
    }
  }, [inputValue, isImageMode, isWebSearchMode, isDeepResearchMode, isQuizMode, attachedMedia, selectedAgent]);

  // Rate limiting state (for non-authenticated users)
  const [dailyMessageCount, setDailyMessageCount] = useState(0);
  const [lastResetDate, setLastResetDate] = useState(new Date().toDateString());
  const [limitExhausted, setLimitExhausted] = useState(false);

  // Credits UI state
  const [showCreditsMenu, setShowCreditsMenu] = useState(false);
  const creditsMenuRef = useRef<HTMLDivElement | null>(null);
  const [creditsRemaining, setCreditsRemaining] = useState<number>(200);
  const [dailyCreditsRemaining, setDailyCreditsRemaining] = useState<number>(300);
  const [creditsTier, setCreditsTier] = useState<string>('free');
  const [creditsResetDate, setCreditsResetDate] = useState<string | undefined>(undefined);
  const [isCreditsLoading, setIsCreditsLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalResetDate, setUpgradeModalResetDate] = useState<string | undefined>(undefined);

  const [requestAbortController, setRequestAbortController] =
    useState<AbortController | null>(null);

  const { toast } = useToast();

  // Apply theme immediately with optimized transitions
  useEffect(() => {
    const root = document.documentElement;

    // Use CSS custom properties for smoother transitions
    // root.style.setProperty("--theme-transition", "all 100ms ease-out");

    // Use batch DOM updates for better performance
    root.classList.toggle("dark", isDarkMode);
  }, [isDarkMode]);

  // Auto-hide header on scroll - simplified
  useEffect(() => {
    let timeoutId: number;

    const handleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        const currentScrollY = window.scrollY;
        if (currentScrollY > lastScrollY && currentScrollY > 100) {
          setIsHeaderVisible(false);
        } else {
          setIsHeaderVisible(true);
        }
        setLastScrollY(currentScrollY);
      }, 100);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
    };
  }, [lastScrollY]);

  // Listen for image modal events from Wikipedia agent
  useEffect(() => {
    const handleOpenImageModal = (event: any) => {
      const { images, index } = event.detail;
      setModalImages(images);
      setModalInitialIndex(index);
      setShowImageModal(true);
    };

    window.addEventListener("openImageModal", handleOpenImageModal);
    return () =>
      window.removeEventListener("openImageModal", handleOpenImageModal);
  }, []);

  // Global click delegation for transcript toggle inside video summary cards
  useEffect(() => {
    const onClick = (e: Event) => {
      try {
        const target = e.target as HTMLElement | null;
        if (!target) return;
        const toggleBtn = target.closest(
          "[data-transcript-toggle]"
        ) as HTMLElement | null;
        if (!toggleBtn) return;
        const section = toggleBtn.closest(
          "[data-transcript-section]"
        ) as HTMLElement | null;
        if (!section) return;

        const content = section.querySelector(
          ".transcript-content"
        ) as HTMLElement | null;
        const icon = toggleBtn.querySelector("svg") as SVGElement | null;
        const label = toggleBtn.querySelector(
          ".toggle-text"
        ) as HTMLElement | null;
        if (!content || !icon || !label) return;

        const isHidden = content.classList.toggle("hidden");
        // Rotate chevron
        icon.classList.toggle("-rotate-90", isHidden);
        icon.classList.toggle("rotate-0", !isHidden);
        // Update label
        label.textContent = isHidden
          ? "Show raw transcript"
          : "Hide raw transcript";
      } catch (err) {
        console.error("Failed to toggle transcript:", err);
      }
    };
    document.addEventListener("click", onClick, { capture: true });
    return () =>
      document.removeEventListener("click", onClick, { capture: true } as any);
  }, []);

  // Global click delegation for YouTube "Summarize" buttons rendered as raw HTML
  useEffect(() => {
    const onClick = (e: Event) => {
      try {
        const target = e.target as HTMLElement | null;
        if (!target) return;
        const btn = target.closest(
          "button[data-summarize-url]"
        ) as HTMLButtonElement | null;
        if (!btn) return;
        const url = btn.getAttribute("data-summarize-url") || "";
        if (!url) return;
        e.preventDefault();
        window.dispatchEvent(
          new CustomEvent("summarizeVideo", { detail: { url } })
        );
      } catch (err) {
        console.error("Failed to handle summarize button click:", err);
      }
    };
    document.addEventListener("click", onClick, { capture: true });
    return () =>
      document.removeEventListener("click", onClick, { capture: true } as any);
  }, []);

  // Listen for selection tooltip events to prefill/focus input
  useEffect(() => {
    const handlePrefill = (event: CustomEvent<{ text: string }>) => {
      try {
        const selected = event.detail?.text || "";
        if (selected) {
          setInputValue((prev) => (prev ? `${prev}\n\n${selected}` : selected));
        }
      } catch (e) {
        // no-op
      }
    };

    window.addEventListener("prefillChatInput", handlePrefill as EventListener);
    return () => {
      window.removeEventListener(
        "prefillChatInput",
        handlePrefill as EventListener
      );
    };
  }, []);

  // Listen for custom events to open the Sources Drawer
  useEffect(() => {
    const handleOpenSourcesDrawer = (event: CustomEvent<any>) => {
      setSourcesDrawerData(event.detail);
      setShowSourcesDrawer(true);
    };

    window.addEventListener(
      "openSourcesDrawer",
      handleOpenSourcesDrawer as EventListener
    );
    return () => {
      window.removeEventListener(
        "openSourcesDrawer",
        handleOpenSourcesDrawer as EventListener
      );
    };
  }, []);

  // Listen for custom events to open the Fullscreen Image Modal from SearchResults
  useEffect(() => {
    const handleOpenFullscreenImageModal = (
      event: CustomEvent<ImageModalData>
    ) => {
      setFullscreenImageData(event.detail);
      setShowFullscreenImageModal(true);
    };

    window.addEventListener(
      "openFullscreenImageModal",
      handleOpenFullscreenImageModal as EventListener
    );
    return () => {
      window.removeEventListener(
        "openFullscreenImageModal",
        handleOpenFullscreenImageModal as EventListener
      );
    };
  }, []);

  // Listen for dislike button press to show feedback form
  useEffect(() => {
    const handleShowFeedbackForm = () => {
      setShowFeedbackForm(true);
    };

    window.addEventListener("showFeedbackForm", handleShowFeedbackForm);
    return () => {
      window.removeEventListener("showFeedbackForm", handleShowFeedbackForm);
    };
  }, []);

  // Load chat from history or Supabase if chatId is present in URL
  useEffect(() => {
    const loadChatFromURL = async () => {
      // Guard: mark as hydrating to avoid URL/session mutations during initial load
      isHydratingRef.current = true;
      // If auth state is still loading, defer any decision (especially redirects)
      if (authLoading) {
        return;
      }
      // Check route params first, then fallback to query params for backward compatibility
      const params = new URLSearchParams(location.search);
      const chatId = routeChatId || params.get("id") || params.get("chatId");

      // If no chatId, or we are already on this chat AND we already have messages, skip reloading
      if (
        !chatId ||
        (currentChatId === chatId && (messages?.length || 0) > 0)
      ) {
        // Nothing to load; clear hydrating so other effects can proceed
        isHydratingRef.current = false;
        return; // Prevent loading multiple times
      }

      try {
        const uuidRegex =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

        if (user && uuidRegex.test(chatId)) {
          // Check if we already have this session loaded
          const sessions = supabaseChatStorage.sessions || [];
          if (!sessions.find((s) => s.id === chatId)) {
            await supabaseChatStorage.refreshSessions();
          }

          // If session does not exist (deleted or invalid), redirect to new chat
          let existsAfterRefresh = false;
          try {
            const { data: sess } = await supabase
              .from('chat_sessions')
              .select('id')
              .eq('id', chatId)
              .eq('user_id', user.id)
              .maybeSingle();
            existsAfterRefresh = !!sess;
          } catch { }
          if (!existsAfterRefresh) {
            // Show toast and redirect to welcome screen if chat not found
            toast({
              title: "Unable to load conversation",
              description: "",
              variant: "destructive",
            });
            isHydratingRef.current = false;
            navigate('/', { replace: true });
            return;
          }

          // Load messages from Supabase
          const msgs = await supabaseChatStorage.loadMessages(chatId);
          setAllMessages(msgs); // Use allMessages for lazy loading
          setCurrentChatId(chatId);
          // Sync ref with loaded session ID
          currentSessionIdRef.current = chatId;
          supabaseChatStorage.setCurrentSessionId(chatId);

          // Mark all loaded messages as already saved
          savedMessageIdsRef.current.clear();
          msgs.forEach((msg) => {
            const messageKey = `${msg.id}-${msg.type}`;
            savedMessageIdsRef.current.add(messageKey);
          });

          const sessionMeta = supabaseChatStorage.sessions.find(
            (s) => s.id === chatId
          );
          if (sessionMeta) {
            setCurrentChatTitle(sessionMeta.title || "");
          }

          // If loaded from old query param format, redirect to new format
          if (params.get("id") || params.get("chatId")) {
            navigate(`/c/${chatId}`, { replace: true });
          }
          return;
        }

        // Fallback to local storage for guests or non-UUID ids
        const chat = storageManager.getSession(chatId);
        if (chat) {
          setAllMessages(chat.messages || []); // Use allMessages for lazy loading
          setCurrentChatId(chatId);
          // Sync ref with loaded session ID
          currentSessionIdRef.current = chatId;
          setSelectedModel(chat.model || "deepseek");
          setCurrentChatTitle(chat.title || "");
          storageManager.updateSession(chatId, {
            updatedAt: new Date().toISOString(),
          });
          // If loaded from old query param format, redirect to new format
          if (params.get("id") || params.get("chatId")) {
            navigate(`/c/${chatId}`, { replace: true });
          }
        } else {
          console.warn("Chat not found");
          // Show toast and redirect to welcome screen if chat not found
          toast({
            title: "Unable to load conversation",
            description: "",
            variant: "destructive",
            duration: 3000,
          });
          isHydratingRef.current = false;
          navigate('/', { replace: true });
          return;
        }
      } catch (error) {
        console.error("Failed to load chat from history:", error);
      } finally {
        // Unset hydrating flag after a delay to prevent immediate re-save of loaded messages
        setTimeout(() => {
          isHydratingRef.current = false;
        }, 100);
      }
    };

    loadChatFromURL();
  }, [location.search, routeChatId, user, authLoading]);

  // Keep the current chat title in sync when switching chats or when sessions refresh
  useEffect(() => {
    try {
      if (!currentChatId) {
        setCurrentChatTitle("");
        return;
      }
      // Authenticated users: read from Supabase sessions metadata
      if (user) {
        const session = supabaseChatStorage.sessions?.find((s) => s.id === currentChatId);
        if (session) {
          setCurrentChatTitle(session.title || "");
          return;
        }
      }
      // Guests: read from local storage
      const local = storageManager.getSession(currentChatId);
      if (local) {
        setCurrentChatTitle(local.title || "");
      }
    } catch (e) {
      // no-op
    }
  }, [currentChatId, user, supabaseChatStorage.sessions]);

  // Save data to storage manager
  useEffect(() => {
    try {
      // Skip saving during initial hydration to avoid URL changes on refresh
      if (isHydratingRef.current) {
        // Still update app settings (theme etc.)
        storage.updateAppSettings({ isDarkMode });
        return;
      }
      // Skip saving in temporary mode
      if (isTempMode) {
        // Only update app settings (theme etc.)
        storage.updateAppSettings({ isDarkMode });
        return;
      }

      // Only use local storage chat sessions for non-authenticated users
      if (!user && messages.length > 0) {
        (async () => {
          let chatId = currentChatId;

          // If no current chat ID, create a new local session
          if (!chatId) {
            const firstUserMessage = messages.find((msg) => msg.type === "user");
            const title =
              firstUserMessage?.content?.slice(0, 50) +
              (firstUserMessage?.content?.length > 50 ? "..." : "") ||
              "New Chat";
            const chat = await storageManager.createSession(title);
            chatId = chat.id;
            setCurrentChatId(chatId);
            // Sync ref with new session ID
            currentSessionIdRef.current = chatId;
            // Update URL with new chat ID
            navigate(`/c/${chatId}`, { replace: true });
          }

          // Update the local session with current messages
          const lastMessage = messages[messages.length - 1];
          storageManager.updateSession(chatId, {
            messages,
            lastMessage: lastMessage?.content || "",
            messageCount: messages.length,
            model: selectedModel,
            date: new Date().toLocaleDateString(),
            timestamp: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
        })();
      }
      // Update app settings
      storage.updateAppSettings({ isDarkMode });
    } catch (error) {
      console.error("Failed to save to storage:", error);
    }
  }, [messages, isDarkMode, selectedModel, currentChatId, user, isTempMode]);

  // Load data from storage and generate random suggestions
  useEffect(() => {
    const loadChats = () => {
      try {
        // When explicitly on /chat/:chatId, skip loading any other active local session
        if (routeChatId) {
          return;
        }
        // Skip loading stored chats in temporary mode
        if (isTempMode) {
          return;
        }

        const activeSession = storageManager.getActiveSession();
        if (activeSession) {
          // Get the latest session data
          const updatedSession = storageManager.getSession(activeSession.id);
          if (updatedSession) {
            setAllMessages(updatedSession.messages || []);
            setCurrentChatId(updatedSession.id);
            // Sync ref with loaded session ID
            currentSessionIdRef.current = updatedSession.id;
          }
        }
      } catch (error) {
        console.error("Error loading chats:", error);
      }
    };

    const handleImageFromChitraAI = () => {
      try {
        const imageData = localStorage.getItem("imageToSend");
        if (imageData) {
          const { url, prompt } = JSON.parse(imageData);
          localStorage.removeItem("imageToSend");
          if (prompt) setInputValue(prompt);

          // Create a dummy file object to match the AttachedMedia interface
          const dummyFile = new File([""], "generated-image.png", {
            type: "image/png",
          });

          setAttachments([
            {
              type: "image",
              url,
              name: "generated-image.png",
              file: dummyFile,
            },
          ]);
          setAttachedMedia({
            type: "image",
            url,
            name: "generated-image.png",
            file: dummyFile,
          });
        }
      } catch (error) {
        console.error("Error processing image from ChitraAI:", error);
      }
    };

    const handleSummaryFromNews = () => {
      try {
        const summaryData = localStorage.getItem("summaryToSend");
        if (summaryData) {
          const { summary, title, source } = JSON.parse(summaryData);
          localStorage.removeItem("summaryToSend");

          // Pre-populate input with a prompt about the summary
          const prompt = `Based on this news article summary :\n\n"${title}"\n\n${summary}\n\nProvide your analysis and insights.`;
          setInputValue(prompt);
        }
      } catch (error) {
        console.error("Error processing summary from news:", error);
      }
    };

    loadChats();
    handleImageFromChitraAI();
    handleSummaryFromNews();
  }, [isTempMode]);

  // Prefill input from prompt query param (e.g., coming from Discover)
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const prompt = params.get('prompt');
      if (prompt && prompt.trim()) {
        setInputValue(prompt);
        // Clean the URL to avoid resubmission on reload
        navigate(location.pathname, { replace: true });
        // Auto-send the message
        setTimeout(() => {
          handleSendClick();
        }, 100);
      }
    } catch (e) {
      // no-op
    }
    // Only react to changes in search string
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Regenerate suggestions when starting new chat
  useEffect(() => {
    if (messages.length === 0) {
      setSuggestions(getRandomSuggestions(4));
    }
  }, [messages.length]);

  // Check and reset daily message count
  useEffect(() => {
    const today = new Date().toDateString();
    const savedCount = localStorage.getItem(LOCAL_STORAGE_KEYS.dailyMessageCount) ||
      localStorage.getItem('dailyMessageCount');
    const savedDate = localStorage.getItem(LOCAL_STORAGE_KEYS.lastResetDate) ||
      localStorage.getItem('lastResetDate');

    if (savedDate !== today) {
      setDailyMessageCount(0);
      setLastResetDate(today);
      localStorage.setItem(LOCAL_STORAGE_KEYS.dailyMessageCount, "0");
      localStorage.setItem(LOCAL_STORAGE_KEYS.lastResetDate, today);
    } else if (savedCount) {
      setDailyMessageCount(parseInt(savedCount));
      setLastResetDate(savedDate || today);
    }
  }, []);

  const incrementMessageCount = () => {
    // Skip message counting in temporary mode
    if (isTempMode) {
      return;
    }

    // Track total message count for ads
    const totalCount =
      parseInt(
        localStorage.getItem(LOCAL_STORAGE_KEYS.totalMessageCount) ||
        localStorage.getItem('totalMessageCount') ||
        "0"
      ) + 1;
    localStorage.setItem(LOCAL_STORAGE_KEYS.totalMessageCount, totalCount.toString());

    // Remove local message counting - authenticated users have unlimited access
    if (!user) {
      const newCount = dailyMessageCount + 1;
      setDailyMessageCount(newCount);
      localStorage.setItem(LOCAL_STORAGE_KEYS.dailyMessageCount, newCount.toString());

      if (newCount >= 5) {
        setLimitExhausted(true);
      }
    }
  };

  const canSendMessage = () => {
    // In temporary mode, always allow sending messages (no limits)
    if (isTempMode) {
      return true;
    }
    return user ? true : dailyMessageCount < 5;
  };

  // Save messages to Supabase - batch save and pair adjacent user+ai
  const saveMessagesToSupabase = async (messages: any[]) => {
    if (!user || !messages.length || isHydratingRef.current) return false;

    try {
      // Use ref for synchronous session ID tracking to prevent multiple session creation
      // Check ref FIRST as it's updated synchronously
      let sessionId = currentSessionIdRef.current || currentChatId;

      // Only create a new session if we truly don't have a valid UUID
      const isValidUUID = sessionId && sessionId.match(
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
      );

      if (!isValidUUID) {
        // If another call is already creating a session, wait for it
        if (isCreatingSessionRef.current) {
          // Wait a bit and retry
          await new Promise(resolve => setTimeout(resolve, 100));
          sessionId = currentSessionIdRef.current;
          if (sessionId && sessionId.match(
            /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
          )) {
            // Session was created by another call, use it
            return await saveMessagesToSupabase(messages); // Retry with valid session
          }
        }

        // Double-check the ref one more time before creating (race condition prevention)
        sessionId = currentSessionIdRef.current;
        if (sessionId && sessionId.match(
          /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
        )) {
          // Session was created by another call, use it
          return await saveMessagesToSupabase(messages); // Retry with valid session
        }

        // Set flag to indicate we're creating a session
        isCreatingSessionRef.current = true;

        try {
          // Create new session with first user message as title
          const firstUserMessage = messages.find(
            (msg) => msg.type === "user" || msg.role === "user"
          );
          const firstContent =
            firstUserMessage?.content?.slice(0, 50) || "New Chat";
          sessionId = await supabaseChatStorage.createSession(
            firstContent,
            {
              agentId: selectedAgent?.id || null,
              agentName: selectedAgent?.name || null,
              model: selectedModel || null,
              mode: isImageMode
                ? 'image'
                : isQuizMode
                  ? 'quiz'
                : isWebSearchMode
                  ? 'web-search'
                  : isDeepResearchMode
                    ? 'deep-research'
                    : selectedAgent
                      ? 'chat-agent'
                      : 'chat-model'
            }
          );
          if (sessionId) {
            // Update both ref (synchronous) and state (async)
            currentSessionIdRef.current = sessionId;
            setCurrentChatId(sessionId);
            setCurrentChatTitle(firstContent);
            navigate(`/c/${sessionId}`, { replace: true });
          }
        } finally {
          // Clear the flag when done
          isCreatingSessionRef.current = false;
        }
      }

      if (!sessionId) return false;

      // Save only new messages that haven't been saved yet
      const unsavedMessages = messages.filter((msg) => {
        const messageKey = `${msg.id}-${msg.type}`;
        return !savedMessageIdsRef.current.has(messageKey) && !msg.isStreaming;
      });

      // Walk through unsaved messages and pair adjacent user+ai messages
      let i = 0;
      while (i < unsavedMessages.length) {
        const current = unsavedMessages[i];
        const next = unsavedMessages[i + 1];
        const isUserThenAI =
          current &&
          next &&
          (current.type === "user" || current.role === "user") &&
          (next.type === "ai" || next.role === "assistant");

        if (isUserThenAI) {
          const successPair = await supabaseChatStorage.saveMessagePair(
            current,
            next,
            sessionId
          );
          if (successPair) {
            const keyUser = `${current.id}-${current.type || "user"}`;
            const keyAi = `${next.id}-${next.type || "ai"}`;
            savedMessageIdsRef.current.add(keyUser);
            savedMessageIdsRef.current.add(keyAi);
          }
          i += 2;
          continue;
        }

        // Fallback: save single message
        const success = await supabaseChatStorage.saveMessage(
          current,
          sessionId
        );
        if (success) {
          const messageKey = `${current.id}-${current.type}`;
          savedMessageIdsRef.current.add(messageKey);
        }
        i += 1;
      }

      return true;
    } catch (error) {
      console.error("Failed to save messages to Supabase:", error);
      return false;
    }
  };

  const saveChatHistory = async (messages: any[]) => {
    if (messages.length === 0 || isHydratingRef.current) return;

    // Skip saving in temporary chat mode
    if (isTempMode) {
      return;
    }

    // Authenticated users: do not auto-save here to avoid race conditions with
    // the explicit saves performed during send. This prevents duplicate session creation.
    if (user) return;

    // Fallback to localStorage (for non-authenticated users or if Supabase fails)
    try {
      const lastUserMessage = messages
        .slice()
        .reverse()
        .find((msg) => msg.role === "user" || msg.type === "user");
      const title =
        lastUserMessage?.content?.slice(0, 50) +
        (lastUserMessage?.content?.length > 50 ? "..." : "") || "New Chat";

      // Get or create chat ID
      let chatId = currentChatId;
      if (!chatId) {
        const newChat = await storageManager.createSession(title);
        chatId = newChat.id;
        setCurrentChatId(chatId);
        // Update URL with new chat ID
        navigate(`/c/${chatId}`, { replace: true });
      }

      // Update the chat session with the latest messages and metadata
      storageManager.updateSession(chatId, {
        title,
        messages,
        lastMessage: lastUserMessage?.content || "",
        messageCount: messages.length,
        model: selectedModel,
        date: new Date().toLocaleDateString(),
        timestamp: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      // Clean up old sessions if we have too many
      const allSessions = storageManager.getAllSessions();
      if (allSessions.length > 30) {
        // Sort by timestamp (newest first) and keep only the 30 most recent
        const sortedSessions = [...allSessions].sort(
          (a, b) =>
            new Date(b.updatedAt || b.timestamp).getTime() -
            new Date(a.updatedAt || a.timestamp).getTime()
        );

        // Clear and recreate sessions with the most recent 30
        storageManager.clearChats();
        sortedSessions.slice(0, 30).forEach((session) => {
          if (session.id === chatId) return; // Skip current chat as it will be added back
          storageManager.createSession(session.title);
          storageManager.updateSession(session.id, session);
        });
      }
    } catch (error) {
      console.error("Failed to save chat history:", error);
    }
  };

  // Save individual messages as they are added - prevents duplicates
  useEffect(() => {
    // Only save if we have messages, not hydrating, and not in temp mode
    if (messages.length > 0 && !isHydratingRef.current && !isTempMode) {
      const timeoutId = setTimeout(() => {
        saveChatHistory(messages);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [messages.length, currentChatId, selectedModel, isTempMode]);

  // Create a new chat from a specific message and open in a new tab
  const handleBranch = useCallback(async (messageId: number) => {
    try {
      const idx = messages.findIndex((m: any) => m.id === messageId);
      if (idx === -1) return;

      const originMessage = messages[idx];
      const originUserMessage = messages.slice(0, idx).reverse().find((m: any) => m.type === 'user' || m.role === 'user');

      const summary = (originUserMessage?.content || originMessage?.content || '').toString().replace(/<[^>]*>/g, '').slice(0, 60);
      const title = `Branched: ${summary}${summary.length >= 60 ? '…' : ''}`;

      // Prepare the initial message for the new chat with branch metadata
      const initialMessage = {
        ...originMessage,
        id: Date.now(),
        timestamp: new Date(),
        branchedFrom: {
          chatId: currentChatId,
          messageId,
          summary: summary,
        },
      } as any;

      // Authenticated path: create Supabase session and save message
      if (user) {
        const newSessionId = await supabaseChatStorage.createSession(
          title,
          {
            agentId: selectedAgent?.id || null,
            agentName: selectedAgent?.name || null,
            model: selectedModel || null,
            mode: isImageMode
              ? 'image'
              : isWebSearchMode
                ? 'web-search'
                : isDeepResearchMode
                  ? 'deep-research'
                  : selectedAgent
                    ? 'chat-agent'
                    : 'chat-model'
          }
        );
        if (newSessionId) {
          // Save the branched message to the new session before opening the tab
          try {
            await supabaseChatStorage.saveMessage(initialMessage, newSessionId);
          } catch (e) {
            console.warn('Failed to pre-save branched message to Supabase:', e);
          }
          const url = `/c/${newSessionId}`;
          window.open(url, '_blank');
          return;
        }
      }

      // Guest/local path: create local session and store messages
      const newLocalSession = await storageManager.createSession(title);
      storageManager.updateSession(newLocalSession.id, {
        messages: [initialMessage],
        lastMessage: (initialMessage.content || '').toString(),
        messageCount: 1,
        model: selectedModel,
        date: new Date().toLocaleDateString(),
        timestamp: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      window.open(`/c/${newLocalSession.id}`, '_blank');
    } catch (err) {
      console.error('Failed to branch chat:', err);
      toast({ title: 'Branch failed', description: 'Could not create a new branched chat', variant: 'destructive' });
    }
  }, [messages, currentChatId, user, selectedModel]);

  const handleSendClick = async () => {
    if ((!inputValue.trim() && !attachedMedia) || isLoading) return;

    if (!canSendMessage()) {
      setLimitExhausted(true);
      return;
    }

    const abortController = new AbortController();
    setRequestAbortController(abortController);

    // Make loader visible immediately on click (MessageHandler can have async prep work)
    setIsLoading(true);

    // Enforce daily quota for authenticated users (free vs pro)
    if (user && !isTempMode) {
      try {
        const primaryAction = isImageMode ? 'image' : 'message';
        const primaryRes = await consumeDailyQuota(primaryAction, 1);
        if (!primaryRes?.allowed) {
          setIsLoading(false);
          setLimitExhausted(true);
          setPricingLimitMessage(isImageMode ? 'Image generation limit reached' : 'Daily message limit reached');
          setShowWelcomePricing(true);
          toast({
            title: 'Daily limit reached',
            description: 'You have reached your daily quota. Please upgrade to continue.',
            variant: 'destructive',
          });
          return;
        }

        if (attachments?.length) {
          const attRes = await consumeDailyQuota('attachment', attachments.length);
          if (!attRes?.allowed) {
            setIsLoading(false);
            setLimitExhausted(true);
            setPricingLimitMessage('Daily attachment limit reached');
            setShowWelcomePricing(true);
            toast({
              title: 'Daily attachment limit reached',
              description: 'You have reached your daily attachment quota. Please upgrade to continue.',
              variant: 'destructive',
            });
            return;
          }
        }

        // Update credits UI
        setCreditsRemaining(primaryRes?.remaining?.messages ?? creditsRemaining);
        setDailyCreditsRemaining(primaryRes?.limits?.messages ?? dailyCreditsRemaining);
      } catch (err) {
        console.error('Failed to enforce daily quota:', err);
      }
    }

    // Lock loader context for this request to avoid switching on model change mid-generation
    try {
      const snapshotQuery = (inputValue || "").trim();
      const name =
        selectedAgent?.name ||
        selectedModel.charAt(0).toUpperCase() + selectedModel.slice(1);
      const context:
        | "image"
        | "chat-agent"
        | "chat-model"
        | "web-search"
        | "deep-research"
        | "quiz" = isImageMode
          ? "image"
          : isQuizMode
            ? "quiz"
            : isWebSearchMode
              ? "web-search"
              : isDeepResearchMode
                ? "deep-research"
                : selectedAgent
                  ? "chat-agent"
                  : "chat-model";
      setLoadingContext({ context, name, userQuery: snapshotQuery });
    } catch { }

    // Keep current attachments and input value in state (no-op but explicit)
    setAttachments(attachments);
    setInputValue(inputValue);

    handleSend();

    setTimeout(() => {
      const messagesContainer = document.querySelector(
        "[data-messages-container]"
      );
      if (messagesContainer) {
        (messagesContainer as HTMLElement).scrollTop = (messagesContainer as HTMLElement).scrollHeight;
      }
    }, 100);
  };

  const handleStopRequest = () => {
    if (requestAbortController) {
      requestAbortController.abort();
      setRequestAbortController(null);
      setIsLoading(false);
      setLoadingContext(null);
    }
  };

  // Wrapper function for MessageHandler - buffer user then pair with next AI
  const saveMessageToSupabase = async (message: any) => {
    // Skip saving in temporary mode or when unauthenticated
    if (isTempMode || !user) return false;

    // Allow callers to force a direct save (do not buffer waiting for a pair)
    // Used when assistant messages are saved server-side (e.g., create-image edge functions)
    if ((message as any)?.forceSingleSave) {
      const msg = { ...(message as any) };
      delete (msg as any).forceSingleSave;
      return await saveMessagesToSupabase([msg]);
    }

    // If user message, buffer it and wait for AI
    if ((message.type === "user" || message.role === "user") && !message.isStreaming) {
      // Enqueue user messages to preserve order, including duplicates
      pendingUserQueueRef.current.push(message);
      return true;
    }

    // If AI message and we have a pending user, save as a pair
    if ((message.type === "ai" || message.role === "assistant") && !message.isStreaming) {
      // In Multi-AI mode, only the first AI response (multiOrder === 1) should pair with the user
      const isMultiBatch = !!message.multiBatchId;
      const shouldPair = !isMultiBatch || (isMultiBatch && (message.multiOrder === 1 || message.multiOrder === undefined));

      if (shouldPair && pendingUserQueueRef.current.length > 0) {
        // Dequeue the oldest pending user to maintain FIFO order
        const userMsg = pendingUserQueueRef.current.shift();
        const success = await saveMessagesToSupabase([userMsg, message]);
        if (success) {
          const keyUser = `${userMsg.id}-${userMsg.type || "user"}`;
          const keyAi = `${message.id}-${message.type || "ai"}`;
          savedMessageIdsRef.current.add(keyUser);
          savedMessageIdsRef.current.add(keyAi);
          return true;
        }
        // If pair save failed, fall through to single save of AI (user already cleared to avoid duplicates)
      }
    }

    // Otherwise save single message
    const successSingle = await saveMessagesToSupabase([message]);
    if (successSingle) {
      const key = `${message.id}-${message.type}`;
      savedMessageIdsRef.current.add(key);
    }
    return successSingle;
  };

  // Initialize message handler
  const { handleSend } = useMessageHandler({
    currentChatId,
    selectedModel,
    isImageMode,
    isWebSearchMode,
    isDeepResearchMode,
    isQuizMode,
    selectedAgent,
    user,
    incrementMessageCount,
    isDarkMode,
    inputValue,
    setInputValue,
    setMessages: setMessagesAndSync, // Use wrapper to sync both states
    setIsLoading,
    setAttachedMedia,
    attachments,
    attachedMedia,
    precomputedFallbackCollage,
    // Add missing required props
    setSelectedMemeTemplate,
    setMemeTemplates,
    setShowMemeTemplates,
    setGifs,
    setShowGifs,
    selectedMemeTemplate,
    showMemeTemplates,
    memeTemplates,
    // Add save function
    saveMessageToSupabase: saveMessageToSupabase,
    // Multi-AI props
    isMultiAIMode,
    selectedMultiAIModels,
  });

  // Clear the locked loading context when loading completes
  useEffect(() => {
    if (!isLoading) {
      setLoadingContext(null);
    }
  }, [isLoading]);

  // Attach summarizeVideo listener after handleSend is available
  useEffect(() => {
    const handleSummarizeVideo = (event: CustomEvent<{ url: string }>) => {
      try {
        const url = event.detail?.url || "";
        if (!url) return;
        const videoSummaryAgent: AIAgent = {
          id: "video-summary",
          name: "Video Summariser",
          description: "Summarize video",
          icon: "https://cdn-icons-png.flaticon.com/512/2989/2989837.png",
          category: "Information",
        };
        setSelectedAgent(videoSummaryAgent);
        setInputValue(url);
        setTimeout(() => {
          void handleSendClick();
        }, 0);
      } catch (e) {
        console.error("Failed to trigger video summary:", e);
      }
    };

    window.addEventListener(
      "summarizeVideo",
      handleSummarizeVideo as EventListener
    );
    return () => {
      window.removeEventListener(
        "summarizeVideo",
        handleSummarizeVideo as EventListener
      );
    };
  }, [handleSend]);

  // Initialize regenerate handler
  const { handleRegenerate } = useRegenerateHandler({
    messages,
    setMessages: setMessagesAndSync, // Use wrapper to sync both states
    setInputValue,
    setIsImageMode,
    setAttachedMedia,
    setSelectedAgent,
    handleSend,
  });

  const handleKeyPress = (e: React.KeyboardEvent) => {
    const isCtrlEnter = e.key === "Enter" && (e.ctrlKey || e.metaKey);

    if (!isCtrlEnter) {
      return;
    }

    e.preventDefault();

    if ((!inputValue.trim() && !attachedMedia) || isLoading) {
      return;
    }

    if (!canSendMessage()) {
      setLimitExhausted(true);
      return;
    }

    void handleSendClick();
  };

  // Global hotkey: Ctrl + Shift + I => Toggle background style (image ↔ solid)
  useEffect(() => {
    const onGlobalKeyDown = (e: KeyboardEvent) => {
      const isCtrlShiftI =
        e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "i");
      if (!isCtrlShiftI) {
        return;
      }

      e.preventDefault();

      const now = Date.now();
      if (now - lastBackgroundToggleRef.current < 2000) {
        return;
      }
      lastBackgroundToggleRef.current = now;

      try {
        const currentSettings = storage.getAppSettings();
        const currentType = (currentSettings?.backgroundType === 'image') ? 'image' : 'solid';
        const nextType: 'solid' | 'image' = currentType === 'image' ? 'solid' : 'image';

        const success = storage.updateAppSettings({ backgroundType: nextType });

        if (!success) {
          toast({
            title: "Background unchanged",
            description:
              "Could not update background preference. Please try again.",
            variant: "destructive",
          });
          return;
        }

        window.dispatchEvent(
          new CustomEvent("appSettingsChanged", {
            detail: { backgroundType: nextType },
          })
        );
        const backgroundDescriptions: Record<'image' | 'solid', string> = {
          image: "Image background enabled.",
          solid: "Solid color background enabled.",
        };
      } catch (error) {
        console.error("Failed to cycle background type:", error);
        toast({
          title: "Background unchanged",
          description: "Something went wrong while updating the background.",
          variant: "destructive",
        });
      }
    };

    window.addEventListener("keydown", onGlobalKeyDown, { capture: false });
    return () =>
      window.removeEventListener(
        "keydown",
        onGlobalKeyDown as any,
        { capture: false } as any
      );
  }, [toast]);

  // Global hotkey: Ctrl + B => Toggle sidebar visibility
  useEffect(() => {
    const onToggleSidebarHotkey = (e: KeyboardEvent) => {
      const isCtrlB = e.ctrlKey && (e.key === "b" || e.key === "B");
      if (!isCtrlB) return;
      e.preventDefault();
      setIsOpen((prev) => !prev);
    };

    window.addEventListener("keydown", onToggleSidebarHotkey, {
      capture: false,
    });
    return () =>
      window.removeEventListener(
        "keydown",
        onToggleSidebarHotkey as any,
        { capture: false } as any
      );
  }, []);

  // Edge swipe disabled - use header button to open sidebar instead
  // This prevents touch event conflicts that can freeze the app on mobile
  useEffect(() => {
    // No edge swipe listeners - keeps mobile smooth
  }, []);

  const handleNewChat = async () => {
    // Clear saved message tracking when starting new chat
    savedMessageIdsRef.current.clear();
    // Clear session ID ref to allow new session creation
    currentSessionIdRef.current = null;
    // Clear persisted active session so home doesn't auto-load previous chat
    try {
      storage.setActiveSession(null);
    } catch { }
    // Clear Supabase current session pointer for authenticated users
    try {
      supabaseChatStorage.setCurrentSessionId(null);
    } catch { }
    // Clear temporary messages if in temp mode
    if (isTempMode) {
      clearTempMessages();
    }

    setAllMessages([]);
    setCurrentChatId(null);
    setInputValue("");
    setAttachedMedia(null);
    setSelectedAgent(null);
    setSelectedModel("chat gpt");
    setIsImageMode(false);
    setIsWebSearchMode(false);
    setIsDeepResearchMode(false);
    setIsQuizMode(false);
    setSuggestions(getRandomSuggestions(4));
    // Ensure we remain on the main chat page when starting a new conversation
    navigate("/", { replace: true });
  };

  // Global hotkey: Ctrl + Shift + O (primary), Ctrl + Alt + O (fallback), Meta + Shift + O (mac)
  useEffect(() => {
    const onNewChatHotkey = (e: KeyboardEvent) => {
      const keyO = e.key === "O" || e.key === "o";
      const isCtrlShiftO = keyO && e.ctrlKey && e.shiftKey;
      const isCtrlAltO = keyO && e.ctrlKey && e.altKey;
      const isMetaShiftO = keyO && e.metaKey && e.shiftKey;
      if (!(isCtrlShiftO || isCtrlAltO || isMetaShiftO)) return;
      e.preventDefault();
      handleNewChat();
    };

    window.addEventListener("keydown", onNewChatHotkey, { capture: false });
    return () =>
      window.removeEventListener(
        "keydown",
        onNewChatHotkey as any,
        { capture: false } as any
      );
  }, [handleNewChat]);

  const handleModeChange = (
    mode: "chat" | "image" | "web" | "deep-research" | "quiz"
  ) => {
    setIsImageMode(mode === "image");
    setIsWebSearchMode(mode === "web");
    setIsDeepResearchMode(mode === "deep-research");
    setIsQuizMode(mode === "quiz");
    if (mode !== "chat") {
      setSelectedAgent(null);
    }
  };

  const toggleTheme = () => {
    const nextTheme = isDarkMode ? "light" : "dark";
    setTheme(nextTheme);
  };

  const shareChat = async () => {
    if (messages.length === 0) return;

    try {
      const chatContent = messages
        .map(
          (msg) =>
            `${msg.type === "user" ? "You" : msg.model?.toUpperCase()}: ${msg.content
            }`
        )
        .join("\n\n");

      if (navigator.share) {
        await navigator.share({
          title: "AI Chat Conversation",
          text: chatContent,
        });
      } else {
        await navigator.clipboard.writeText(chatContent);
      }
    } catch (error) {
      console.error("Failed to share chat:", error);
    }
  };

  // Click outside handler for chat menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (chatMenuRef.current && !chatMenuRef.current.contains(event.target as Node)) {
        setShowChatMenu(false);
      }
    };

    if (showChatMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showChatMenu]);

  // Handle rename chat
  const handleRenameChat = async () => {
    if (!newChatName.trim() || !currentChatId) return;

    try {
      if (user) {
        const { error } = await supabase
          .from('chat_sessions')
          .update({ title: newChatName.trim() })
          .eq('id', currentChatId)
          .eq('user_id', user.id);

        if (error) throw error;

        // Refresh sessions to update sidebar
        await supabaseChatStorage.refreshSessions();
      } else {
        // Update local storage
        storageManager.updateSession(currentChatId, { title: newChatName.trim() });
      }

      setCurrentChatTitle(newChatName.trim());
      setShowRenameModal(false);
      setNewChatName("");
      setShowChatMenu(false);

    } catch (error) {
      console.error('Failed to rename chat:', error);
      toast({
        title: "Error",
        description: "Failed to rename chat",
        variant: "destructive",
      });
    }
  };

  // Handle delete chat
  const handleDeleteChat = async () => {
    if (!currentChatId) return;

    setIsDeleting(true);
    try {
      if (user) {
        const { error } = await supabase
          .from('chat_sessions')
          .delete()
          .eq('id', currentChatId)
          .eq('user_id', user.id);

        if (error) throw error;

        // Refresh sessions to update sidebar
        await supabaseChatStorage.refreshSessions();
      } else {
        // Delete from local storage
        storageManager.deleteSession(currentChatId);
        const savedChats = JSON.parse(localStorage.getItem('chatHistory') || '[]');
        const updatedChats = savedChats.filter((session: any) => session.id !== currentChatId);
        localStorage.setItem('chatHistory', JSON.stringify(updatedChats));
      }

      setShowDeleteModal(false);
      setShowChatMenu(false);

      // Navigate to home and start new chat
      handleNewChat();
    } catch (error) {
      console.error('Failed to delete chat:', error);
      toast({
        title: "Error",
        description: "Failed to delete chat",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAuthSuccess = (userData: any) => {
    setLimitExhausted(false);
    setDailyMessageCount(0);
  };

  const handleLogout = async () => {
    await signOut();
    setLimitExhausted(false);
    setDailyMessageCount(0);
  };

  // Debug authentication state
  useEffect(() => {
    // Log auth state changes for debugging
  }, [user, authLoading, session]);

  const handleEditMessage = (content: string) => {
    setInputValue(content);
    // Scroll to input area
    const inputElement = document.querySelector("textarea");
    if (inputElement) {
      inputElement.scrollIntoView({ behavior: "smooth" });
      inputElement.focus();
    }
  };

  // Branch the chat starting from a specific AI message (with its preceding user message)
  const handleBranchFromMessage = async (messageId: number) => {
    try {
      // Start transition animation
      setIsBranchingTransition(true);
      // Find AI message by id
      const aiIndex = messages.findIndex((m: any) => m.id === messageId);
      if (aiIndex === -1) return;

      const aiMsg = messages[aiIndex];
      // Find the most recent preceding user message
      let userMsg: any | null = null;
      for (let i = aiIndex - 1; i >= 0; i--) {
        if (
          (messages[i] as any).type === "user" ||
          (messages[i] as any).role === "user"
        ) {
          userMsg = messages[i];
          break;
        }
      }

      // Add branchedFrom metadata to the AI message so footer renders in new chat
      const summarySource = (userMsg?.content || aiMsg?.content || '').toString().replace(/<[^>]*>/g, '').slice(0, 60);
      const aiMsgWithBranch = {
        ...aiMsg,
        branchedFrom: {
          chatId: currentChatId,
          messageId,
          summary: summarySource,
        },
      } as any;

      const seedMessages = userMsg ? [userMsg, aiMsgWithBranch] : [aiMsgWithBranch];
      const titleSource = (userMsg?.content ||
        aiMsg?.content ||
        "New Chat") as string;
      const title =
        titleSource.slice(0, 50) + (titleSource.length > 50 ? "..." : "");
      const modelToUse = (aiMsg?.model || selectedModel) as string;

      // Clear saved message tracking since we're starting a fresh chat context
      savedMessageIdsRef.current.clear();

      if (isTempMode) {
        // In temp mode, just replace current messages without persistence
        setAllMessages(seedMessages as any);
        setSelectedModel(modelToUse);
        setCurrentChatId(null);
        // Clear session ID ref for temp mode
        currentSessionIdRef.current = null;
        // Navigate to new temp chat with smooth transition
        navigate("/", { replace: true });
        toast({
          title: "Branched to new temporary chat",
          description: "Starting fresh conversation...",
        });
        return;
      }

      if (user) {
        // Authenticated: create Supabase session and save pair
        const newId = await supabaseChatStorage.createSession(
          title,
          {
            agentId: selectedAgent?.id || null,
            agentName: selectedAgent?.name || null,
            model: selectedModel || null,
            mode: isImageMode
              ? 'image'
              : isWebSearchMode
                ? 'web-search'
                : isDeepResearchMode
                  ? 'deep-research'
                  : selectedAgent
                    ? 'chat-agent'
                    : 'chat-model'
          }
        );
        if (newId) {
          setCurrentChatId(newId);
          // Sync ref with new session ID
          currentSessionIdRef.current = newId;
          supabaseChatStorage.setCurrentSessionId(newId);
          setAllMessages(seedMessages as any);
          setSelectedModel(modelToUse);
          // Navigate to new chat with smooth transition
          navigate(`/c/${newId}`, { replace: true });

          // Persist immediately
          if (userMsg) {
            await supabaseChatStorage.saveMessagePair(
              userMsg as any,
              aiMsg as any,
              newId
            );
          } else {
            await supabaseChatStorage.saveMessage(aiMsg as any, newId);
          }
          toast({
            title: "Branched to new chat",
            description: "Navigating to your new conversation...",
          });
          return;
        }
        // Fallback to local if Supabase session creation failed
      }

      // Guest/local storage path
      const chat = await storageManager.createSession(title);
      const chatId = chat.id;
      setCurrentChatId(chatId);
      // Sync ref with new session ID
      currentSessionIdRef.current = chatId;
      setAllMessages(seedMessages as any);
      setSelectedModel(modelToUse);
      // Navigate to new chat with smooth transition
      navigate(`/c/${chatId}`, { replace: true });
      storageManager.updateSession(chatId, {
        title,
        messages: seedMessages as any,
        lastMessage:
          (seedMessages[seedMessages.length - 1] as any)?.content || "",
        messageCount: seedMessages.length,
        model: modelToUse,
        date: new Date().toLocaleDateString(),
        timestamp: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      toast({
        title: "Branched to new chat",
        description: "Navigating to your new conversation...",
      });
    } catch (error) {
      console.error("Failed to branch chat:", error);
      toast({
        title: "Branch failed",
        description: "Could not create new chat branch",
        variant: "destructive",
      });
    } finally {
      // End transition animation after a brief delay
      setTimeout(() => {
        setIsBranchingTransition(false);
      }, 500);
    }
  };

  // Handle "Ask Other AI" - send the preceding USER query of an AI message to another AI model
  const handleAskOtherAI = useCallback(async (aiMessageId: number, targetModel: string) => {
    try {
      // Locate the AI message and its preceding USER query
      const aiIndex = (messages as any[]).findIndex((m: any) => m.id === aiMessageId);
      if (aiIndex === -1) {
        toast({ title: "Message not found", description: "Could not locate the selected message.", variant: "destructive" });
        return;
      }

      // Find the most recent preceding user message
      let userQuery: string | null = null;
      for (let i = aiIndex - 1; i >= 0; i--) {
        const m = (messages as any[])[i] as any;
        if (m?.type === "user" || m?.role === "user") {
          userQuery = (m.content || "").toString();
          break;
        }
      }

      // Fallback: if no preceding user message, use the AI content (last resort)
      const inputText = (userQuery ?? ((messages as any[])[aiIndex]?.content || "")).toString();
      if (!inputText.trim()) {
        toast({ title: "Nothing to send", description: "No associated user query was found.", variant: "destructive" });
        return;
      }

      // Set the input value with the USER's query and prep auto-send
      setInputValue(inputText);

      // Switch to the selected AI model
      setSelectedModel(targetModel);

      // Clear any modes to ensure normal chat
      setIsImageMode(false);
      setIsWebSearchMode(false);
      setIsDeepResearchMode(false);
      setSelectedAgent(null);

      // Use unified auto-send mechanism to avoid races
      autoSendRef.current = {
        shouldSend: true,
        message: inputText,
        mode: 'chat',
        media: null,
      };

      // Show toast notification
      // toast({
      //   title: "Asking " + targetModel.charAt(0).toUpperCase() + targetModel.slice(1),
      //   description: "Sending the original user query to the selected AI...",
      // });

      // No need to call handleSendClick directly; effect will auto-send
    } catch (error) {
      console.error("Failed to ask other AI:", error);
      toast({
        title: "Error",
        description: "Failed to send message to other AI. Please try again.",
        variant: "destructive",
      });
    }
  }, [messages, setInputValue, setSelectedModel, setIsImageMode, setIsWebSearchMode, setIsDeepResearchMode, setSelectedAgent, toast]);

  const handleSelectChat = async (chatId: string) => {
    // Prevent loading the same chat twice
    if (currentChatId === chatId) return;

    // Clear saved message IDs when switching chats
    savedMessageIdsRef.current.clear();

    // Reset lazy loading when switching chats
    resetLazyLoading();

    if (user) {
      // Load from Supabase for authenticated users
      try {
        const messages = await supabaseChatStorage.loadMessages(chatId);
        setAllMessages(messages); // Use allMessages for lazy loading
        setCurrentChatId(chatId);
        // Sync ref with loaded session ID
        currentSessionIdRef.current = chatId;
        supabaseChatStorage.setCurrentSessionId(chatId);

        // Mark loaded messages as saved
        messages.forEach((msg) => {
          const messageKey = `${msg.id}-${msg.type}`;
          savedMessageIdsRef.current.add(messageKey);
        });

        // Refresh sessions to ensure we have up-to-date title
        await supabaseChatStorage.refreshSessions();
        // Get session details for title
        const session = supabaseChatStorage.sessions.find((s) => s.id === chatId);
        if (session) {
          // Also update the current chat title immediately for the header menu
          setCurrentChatTitle(session.title || "");
        } else {
          // Fallback: derive a title from the first user message if session meta isn't available yet
          const firstUserMessage = messages.find((m: any) => m.type === 'user');
          if (firstUserMessage?.content) {
            const derived = firstUserMessage.content.slice(0, 50) + (firstUserMessage.content.length > 50 ? '...' : '');
            setCurrentChatTitle(derived);
          } else {
            setCurrentChatTitle("");
          }
        }

        // Navigate to chat route using React Router
        navigate(`/c/${chatId}`);
        return;
      } catch (error) {
        console.error("Failed to load chat from Supabase:", error);
      }
    }

    // Fallback to localStorage
    const chat = storageManager.getSession(chatId);
    if (chat) {
      setAllMessages(chat.messages || []); // Use allMessages for lazy loading
      setCurrentChatId(chatId);
      // Sync ref with loaded session ID
      currentSessionIdRef.current = chatId;
      setSelectedModel(chat.model || "deepseek");
      // Update current chat title for header menu in guest mode
      setCurrentChatTitle(chat.title || "");
      // Navigate to chat route using React Router
      navigate(`/c/${chatId}`);
      storageManager.updateSession(chatId, {
        updatedAt: new Date().toISOString(),
      });
    }
  };

  return (
    <>
      <ChatRedirect />
      <SEOHead
        title={routeChatId && currentChatTitle ? currentChatTitle : "Oreog"}
        description="Experience the future of AI conversation with our multi-model chat platform. Chat with Claude, GPT-4, Gemini, LLaMA, and Mixtral. Generate images, get intelligent responses, and explore AI capabilities for free."
        keywords="AI chat, chatbot, artificial intelligence, GPT-4, Claude, Gemini, LLaMA, Mixtral, AI models, image generation, conversation AI, machine learning, free AI, chat assistant"
      />

      <div
        className={`min-h-screen flex transition-colors duration-150 bg-transparent ${isDarkMode ? "text-white" : "text-gray-900"
          }`}
      >
        <BackgroundWaves isDarkMode={isDarkMode} />

        {/* Fixed Sidebar */}
        <Sidebar
          isOpen={isOpen}
          onToggle={() => setIsOpen(!isOpen)}
          onAuth={() => setShowAuth(true)}
          user={user}
          onNewChat={handleNewChat}
          isDarkMode={isDarkMode}
          dailyMessageCount={dailyMessageCount}
          onSelectChat={handleSelectChat}
          planTier={creditsTier}
          onUpgradeNeeded={(message) => {
            setPricingLimitMessage(message);
            setShowWelcomePricing(true);
          }}
        />

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col relative w-full bg-transparent transition-all duration-200">
          {/* Fixed Header */}
          <header
            className={`fixed top-0 right-0 left-0 z-20 transition-all duration-300 ${isHeaderVisible && !isVoiceModeActive ? "translate-y-0" : "-translate-y-full"
              }`}
          >
            <div className="relative flex items-center justify-between px-1 py-1 sm:px-4">
              <div className="flex items-center gap-2 sm:gap-4 border-grey-200 rounded-lg">
                {/* Sidebar toggle button */}
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  aria-label="Toggle sidebar"
                  className={cn(
                    "p-1 transition-colors duration-150 rounded-xl flex items-center justify-center",
                    isDarkMode
                      ? "text-slate-300 hover:text-white hover:bg-white/10"
                      : "text-black/90 hover:text-slate-900 hover:bg-slate-200/70"
                  )}
                >
                  {isTempMode ? (
                    <>
                      <span className="sm:hidden">
                        <LookAroundIcon size={24} />
                      </span>
                      <SidepanelIcon
                        className="h-6 w-6 hidden sm:block"
                        isDarkMode={isDarkMode}
                      />
                    </>
                  ) : (
                    <SidepanelIcon
                      className="h-6 w-6"
                      isDarkMode={isDarkMode}
                    />
                  )}
                </button>
              </div>

              {/* Centered Temporary Chat Header (absolute center) */}
              {isTempMode && user && (
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 hidden sm:block">
                  <TempChatHeader isDarkMode={isDarkMode} />
                </div>
              )}
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="relative" ref={creditsMenuRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreditsMenu((v) => {
                        const next = !v;
                        if (next) {
                          void refreshCredits();
                        }
                        return next;
                      });
                    }}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2.5 rounded-2xl text-sm font-medium transition-colors backdrop-blur-sm bg-transparent',
                      backgroundType === 'image'
                        ? (isDarkMode
                          ? 'border-white/10 text-white hover:bg-white/5'
                          : 'border-black/10 text-black hover:bg-white/40')
                        : (isDarkMode
                          ? 'text-white hover:bg-[#383838]'
                          : 'text-gray-900 hover:bg-gray-100')
                    )}
                  >
                    <Sparkles className={cn('w-4 h-4', isDarkMode ? 'text-gray-300' : 'text-gray-600')} />
                    {isCreditsLoading ? (
                      ""
                    ) : (
                      <span className="tabular-nums select-none">{creditsRemaining}</span>
                    )}
                  </button>

                  {showCreditsMenu && (
                    <div
                      className={cn(
                        // LAYOUT FIX: Adjust positioning on very small screens so the menu stays fully visible
                        'absolute right-[-120px] xs:right-0 top-full mt-2 w-[300px] sm:w-[320px] rounded-t-2xl border-x border-t z-[80]',
                        isDarkMode ? 'bg-[#202020] border-white/10 text-white' : 'bg-[#F3F4F6] border-gray-200 text-gray-900'
                      )}
                      style={{
                        // PAPERCUT STYLE: Creates the scalloped "receipt" bottom edge
                        WebkitMaskImage:
                          'linear-gradient(to bottom, black calc(100% - 10px), transparent 100%), radial-gradient(circle 10px at bottom, transparent 6px, black 7px)',
                        maskImage:
                          'linear-gradient(to bottom, black calc(100% - 10px), transparent 100%), radial-gradient(circle 10px at bottom, transparent 6px, black 7px)',
                        WebkitMaskSize: '100% 100%, 20px 20px',
                        maskSize: '100% 100%, 20px 20px',
                        WebkitMaskPosition: '0 0, 0 100%',
                        maskPosition: '0 0, 0 100%',
                        WebkitMaskRepeat: 'no-repeat, repeat-x',
                        maskRepeat: 'no-repeat, repeat-x',
                        WebkitMaskComposite: 'source-over, source-over',
                        maskComposite: 'add',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className={cn('p-4 border-b flex items-center justify-between', isDarkMode ? 'border-white/5' : 'border-gray-300')}>
                        <div className="text-sm font-semibold">{creditsTier === 'pro' ? 'Pro' : 'Free'}</div>
                        {creditsTier !== 'pro' && (
                          <button
                            type="button"
                            onClick={() => {
                              setShowCreditsMenu(false);
                              navigate('/upgrade');
                            }}
                            className={cn(
                              'px-3 py-2 rounded-full text-xs font-semibold transition-colors',
                              isDarkMode ? 'bg-white text-black hover:bg-white/90' : 'bg-black text-white hover:bg-black/90'
                            )}
                          >
                            Upgrade
                          </button>
                        )}
                      </div>

                      {/* Added pb-6 to ensure content doesn't hit the papercut edge */}
                      <div className="p-4 select-none pb-6 space-y-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <Sparkles className={cn('w-5 h-5 mt-0.5', isDarkMode ? 'text-gray-300' : 'text-gray-600')} />
                            <div>
                              <div className="text-sm font-semibold">Credits Remaining</div>
                            </div>
                          </div>
                          <div className="text-sm font-semibold tabular-nums">{creditsRemaining}</div>
                        </div>

                        <div className={cn('pt-4 border-t flex items-start justify-between', isDarkMode ? 'border-white/5' : 'border-gray-300')}>
                          <div className="flex items-start gap-3">
                            <CalendarDays className={cn('w-5 h-5 mt-0.5', isDarkMode ? 'text-gray-300' : 'text-gray-600')} />
                            <div>
                              <div className="text-sm font-semibold">Daily credits</div>
                              <div className={cn('text-xs', isDarkMode ? 'text-gray-400' : 'text-gray-600')}>
                                {/* Resets daily */}
                                {/* {creditsResetDate ? `Resets ${new Date(creditsResetDate).toLocaleString('en-IN')}` : 'Resets daily'} */}
                              </div>
                            </div>
                          </div>
                          <div className="text-sm font-semibold tabular-nums">{dailyCreditsRemaining}</div>
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            setShowCreditsMenu(false);
                            navigate('/settings/data');
                          }}
                          className={cn(
                            'w-full flex items-center justify-between text-sm font-medium rounded-xl px-3 py-2 transition-colors',
                            isDarkMode ? 'text-gray-200 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-200'
                          )}
                        >
                          <span>View usage</span>
                          <ChevronRight className={cn('w-4 h-4', isDarkMode ? 'text-gray-400' : 'text-gray-500')} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <ModelSelector
                  selectedModel={selectedModel}
                  onModelChange={setSelectedModel}
                  isDarkMode={isDarkMode}
                  isMultiAIMode={isMultiAIMode}
                  selectedMultiAIModels={selectedMultiAIModels}
                  onToggleMultiAIMode={setIsMultiAIMode}
                  onUpdateMultiAIModels={setSelectedMultiAIModels}
                  isImageMode={backgroundType === 'image'}
                />

                {user && (
                  <div className="relative" ref={chatMenuRef}>
                    <button
                      onClick={() => setShowChatMenu(!showChatMenu)}
                      className={`relative group border-none transition-colors p-3 duration-150 rounded-2xl backdrop-blur-sm bg-transparent ${backgroundType === 'image'
                        ? isDarkMode
                          ? "bg-transparent hover:bg-white/5 text-white border border-white/10 backdrop-blur-sm"
                          : "bg-transparent hover:bg-white/40 text-black border border-black/10 backdrop-blur-sm"
                        : isDarkMode
                          ? "sm:bg-transparent text-white hover:text-gray-200 hover:bg-[#383838]"
                          : "text-[#121212] sm:bg-transparent hover:text-gray-900 hover:bg-white"
                        }`}
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>

                    {/* Chat Menu Dropdown */}
                    {showChatMenu && (
                      <div
                        className={`absolute  overflow-hidden right-0 top-full mt-2 w-56 rounded-2xl border shadow-xl z-[70] ${isDarkMode
                          ? "bg-[#202020] border-[#2e2e2e] text-gray-200"
                          : "bg-white border-gray-200 text-gray-800"
                          }`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Chat Name Display (only when not welcome and not temp) */}
                        {!isTempMode && messages.length > 0 && (
                          <div className={`px-4 py-2 border-b flex items-center justify-between ${isDarkMode ? "border-[#2e2e2e]" : "border-gray-200"
                            }`}>
                            <p className={`text-sm font-medium truncate select-none  opacity-60 ${isDarkMode ? "text-white" : "text-gray-900"
                              }`}>
                              {currentChatTitle || "Untitled Chat"}
                            </p>
                            <button
                              type="button"
                              className={`p-2 rounded-xl transition-colors ${isDarkMode ? "hover:bg-[#2a2a2a] text-gray-300" : "hover:bg-gray-100 text-gray-700"}`}
                              onClick={() => {
                                setNewChatName(currentChatTitle);
                                setShowRenameModal(true);
                                setShowChatMenu(false);
                              }}
                            >
                              <EditIcon className="w-4 h-4" />
                            </button>
                          </div>
                        )}

                        {/* Menu Options */}
                        <div className="">
                          {/* Welcome screen menu */}
                          {messages.length === 0 ? (
                            <>
                              {/* Incognito (only when not already temp) */}
                              {!isTempMode && (
                                <button
                                  type="button"
                                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDarkMode ? "hover:bg-[#2a2a2a]" : "hover:bg-gray-100"
                                    }`}
                                  onClick={() => {
                                    const isPro = creditsTier?.toLowerCase() === 'pro' || creditsTier?.toLowerCase() === 'enterprise' || creditsTier?.toLowerCase() === 'starter';
                                    if (!isPro) {
                                      setPricingLimitMessage("Upgrade to access Incognito mode");
                                      setShowWelcomePricing(true);
                                      setShowChatMenu(false);
                                      return;
                                    }
                                    startIncognitoSession();
                                    setShowChatMenu(false);
                                  }}
                                >
                                  <LookAroundIcon size={20} />
                                  <span>Incognito mode</span>
                                </button>
                              )}



                              {/* New chat */}
                              <button
                                type="button"
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDarkMode ? "hover:bg-[#2a2a2a]" : "hover:bg-gray-100"
                                  }`}
                                onClick={() => {
                                  if (isTempMode) {
                                    setTempMode(false);
                                    clearTempMessages();
                                  }
                                  handleNewChat();
                                  setShowChatMenu(false);
                                }}
                              >
                                <SquarePen className="w-4 h-4" />
                                <span>New chat</span>
                              </button>
                            </>
                          ) : (
                            <>
                              {/* Normal chat (non-temp) */}
                              {!isTempMode && (
                                <>
                                  {/* New chat */}
                                  <button
                                    type="button"
                                    className={`w-full flex select-none items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDarkMode ? "hover:bg-[#2a2a2a]" : "hover:bg-gray-100"
                                      }`}
                                    onClick={() => {
                                      if (isTempMode) {
                                        setTempMode(false);
                                        clearTempMessages();
                                      }
                                      handleNewChat();
                                      setShowChatMenu(false);
                                    }}
                                  >
                                    <SquarePen className="w-4 h-4" />
                                    <span>New chat</span>
                                  </button>

                                  {/* Share */}
                                  <button
                                    type="button"
                                    className={`w-full select-none flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDarkMode ? "hover:bg-[#2a2a2a]" : "hover:bg-gray-100"
                                      }`}
                                    onClick={() => {
                                      setShowShareDialog(true);
                                      setShowChatMenu(false);
                                    }}
                                  >
                                    <Share2 className="w-4 h-4" />
                                    <span>Share</span>
                                  </button>

                                  {/* Files in this chat */}
                                  <button
                                    type="button"
                                    className={`w-full select-none flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDarkMode ? "hover:bg-[#2a2a2a]" : "hover:bg-gray-100"}`}
                                    onClick={() => {
                                      setFilesModalPurpose('view');
                                      setShowFilesModal(true);
                                      setShowChatMenu(false);
                                    }}
                                  >
                                    <Files className="w-4 h-4" />
                                    <span>Files in this chat</span>
                                  </button>
                                </>
                              )}

                              {/* Delete (hide in temp mode) */}
                              {!isTempMode && (
                                <button
                                  type="button"
                                  className={`w-full flex select-none items-center gap-3 px-4 py-2.5 text-sm text-red-500 transition-colors ${isDarkMode ? "hover:bg-[#2a2a2a]" : "hover:bg-gray-100"
                                    }`}
                                  onClick={() => {
                                    setShowDeleteModal(true);
                                    setShowChatMenu(false);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                  <span>Delete</span>
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Limit Exhausted Warning */}
          {limitExhausted && !user && (
            <LimitCard
              isDarkMode={isDarkMode}
              setShowAuth={setShowAuth}
              onClose={() => setLimitExhausted(false)}
            />
          )}

          {/* Messages Area */}
          <div className="flex-1 flex flex-col pb-24 overflow-y-auto pt-16 px-0 sm:px-4 md:px-6 lg:px-12 scrollbar-none relative">
            {/* Temporary Mode Background */}

            {/* Branching Transition Overlay */}
            {isBranchingTransition && (
              <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm transition-all duration-300">
                <div
                  className={`p-6 rounded-2xl shadow-lg ${isDarkMode ? "bg-[#1c1c1c]" : "bg-white"
                    }`}
                >
                  <svg
                    className={`h-8 w-8 animate-spin ${isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    viewBox="0 0 256 256"
                    stroke="currentColor"
                  >
                    <line
                      x1="128"
                      y1="32"
                      x2="128"
                      y2="64"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="24"
                    />
                    <line
                      x1="195.9"
                      y1="60.1"
                      x2="173.3"
                      y2="82.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="24"
                    />
                    <line
                      x1="224"
                      y1="128"
                      x2="192"
                      y2="128"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="24"
                    />
                    <line
                      x1="195.9"
                      y1="195.9"
                      x2="173.3"
                      y2="173.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="24"
                    />
                    <line
                      x1="128"
                      y1="224"
                      x2="128"
                      y2="192"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="24"
                    />
                    <line
                      x1="60.1"
                      y1="195.9"
                      x2="82.7"
                      y2="173.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="24"
                    />
                    <line
                      x1="32"
                      y1="128"
                      x2="64"
                      y2="128"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="24"
                    />
                    <line
                      x1="60.1"
                      y1="60.1"
                      x2="82.7"
                      y2="82.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="24"
                    />
                  </svg>
                </div>
              </div>
            )}

            {isLoading && messages.length === 0 ? (
              <IndexSkeletonLoader isDarkMode={isDarkMode} />
            ) : messages.length === 0 ? (
              <WelcomeScreen
                isDarkMode={isDarkMode}
                selectedAgent={selectedAgent}
                inputArea={
                  <ChatInputArea
                    inputValue={inputValue}
                    onInputChange={setInputValue}
                    attachments={attachments}
                    onAttachmentsChange={setAttachments}
                    isImageMode={isImageMode}
                    isWebSearchMode={isWebSearchMode}
                    isDeepResearchMode={isDeepResearchMode}
                    isQuizMode={isQuizMode}
                    onModeChange={handleModeChange}
                    selectedAgent={selectedAgent}
                    onAgentChange={setSelectedAgent}
                    onSend={handleSendClick}
                    onStop={handleStopRequest}
                    onKeyPress={handleKeyPress}
                    isLoading={isLoading}
                    canSendMessage={canSendMessage()}
                    isDarkMode={isDarkMode}
                    agentDropdownPlacement="bottom"
                    hasLibraryImages={hasLibraryImages}
                    onOpenLibrary={() => {
                      setFilesModalPurpose('pick');
                      setLibrarySelectedUrls([]);
                      setShowFilesModal(true);
                    }}
                    showMemeTemplates={showMemeTemplates}
                    selectedMemeTemplate={selectedMemeTemplate}
                    fixedHeightPx={130}
                    isVoiceModeOpen={isVoiceModeActive}
                    onVoiceModeChange={setIsVoiceModeActive}
                    isWelcomeScreen={true}
                    onExpandedChange={setIsWelcomeInputExpanded}
                  />
                }
                onOpenPricing={() => setShowWelcomePricing(true)}
                onUpgrade={() => navigate('/upgrade')}
                onSuggestionClick={(suggestion) => {
                  setInputValue(suggestion);
                  // Auto-focus the input after setting the value
                  setTimeout(() => {
                    const inputElement = document.querySelector('textarea[placeholder*="Message"]') as HTMLTextAreaElement;
                    if (inputElement) {
                      inputElement.focus();
                    }
                  }, 100);
                }}
                user={user}
                isTempMode={isTempMode}
                planTier={creditsTier}
                isLoadingPlan={isCreditsLoading}
                isInputExpanded={isWelcomeInputExpanded}
              />
            ) : (
              <div className="flex-1 overflow-y-auto pt-2 px-1 xs:px-2 sm:px-4 overflow-x-hidden">
                <ChatInterface
                  messages={messages}
                  isLoading={isLoading}
                  isAttachmentUploading={isAttachmentUploadingGlobal}
                  isDarkMode={isDarkMode}
                  isImageMode={isImageMode}
                  isWebSearchMode={isWebSearchMode}
                  isDeepResearchMode={isDeepResearchMode}
                  selectedModel={selectedModel}
                  selectedAgent={selectedAgent}
                  onRegenerate={handleRegenerate}
                  onShare={shareChat}
                  onEdit={handleEditMessage}
                  onBranch={handleBranchFromMessage}
                  onAskOtherAI={handleAskOtherAI}
                  loadingContext={loadingContext}
                  hasMoreMessages={hasMore}
                  isLoadingMore={isLoadingMore}
                  onLoadMore={loadMore}
                />
              </div>
            )}
          </div>

          {/* Fixed Input Area */}
          {messages.length > 0 && (
            <div
              ref={inputBarRef}
              className="fixed left-0 right-0 z-20 px-[4px] xs:px-2 flex flex-col items-center justify-end pointer-events-none"
              style={{
                bottom: 4,
                maxHeight: '100vh',
              }}
            >
              <ScrollToBottomButton
                isDarkMode={isDarkMode}
                staticPosition={true}
                className="pointer-events-auto"
              />
              <div className="w-full pointer-events-auto">
                <ChatInputArea
                  inputValue={inputValue}
                  onInputChange={setInputValue}
                  attachments={attachments}
                  onAttachmentsChange={setAttachments}
                  isImageMode={isImageMode}
                  isWebSearchMode={isWebSearchMode}
                  isDeepResearchMode={isDeepResearchMode}
                  onModeChange={handleModeChange}
                  selectedAgent={selectedAgent}
                  onAgentChange={setSelectedAgent}
                  onSend={handleSendClick}
                  onStop={handleStopRequest}
                  onKeyPress={handleKeyPress}
                  isLoading={isLoading}
                  canSendMessage={canSendMessage()}
                  isDarkMode={isDarkMode}
                  hasLibraryImages={hasLibraryImages}
                  onOpenLibrary={() => {
                    setFilesModalPurpose('pick');
                    setLibrarySelectedUrls([]);
                    setShowFilesModal(true);
                  }}
                  showMemeTemplates={showMemeTemplates}
                  selectedMemeTemplate={selectedMemeTemplate}
                  isVoiceModeOpen={isVoiceModeActive}
                  onVoiceModeChange={setIsVoiceModeActive}
                />
              </div>
            </div>
          )}
        </div>

        <AuthModal
          isOpen={showAuth}
          onClose={() => setShowAuth(false)}
          onAuth={handleAuthSuccess}
          isDarkMode={isDarkMode}
        />

        <PricingModal
          isOpen={showWelcomePricing}
          onClose={() => {
            setShowWelcomePricing(false);
            setPricingLimitMessage(undefined);
          }}
          planTier={creditsTier}
          limitReachedMessage={pricingLimitMessage}
        />

        <AdModal isDarkMode={isDarkMode} />

        <ChatHistoryModal
          isOpen={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          onSelectChat={handleSelectChat}
          isDarkMode={isDarkMode}
        />

        {showSourcesDrawer && sourcesDrawerData && (
          <SourcesDrawer
            sources={sourcesDrawerData.sources}
            searchDepth={sourcesDrawerData.searchDepth}
            responseTime={sourcesDrawerData.responseTime}
            isDarkMode={sourcesDrawerData.isDarkMode}
            isOpen={showSourcesDrawer}
            onClose={() => setShowSourcesDrawer(false)}
          />
        )}

        {/* Render Full-screen Image Carousel Modal */}
        {showFullscreenImageModal && fullscreenImageData && (
          <ImageCarousel
            images={fullscreenImageData.images}
            isDarkMode={isDarkMode}
            isOpen={showFullscreenImageModal}
            onClose={() => setShowFullscreenImageModal(false)}
            initialIndex={fullscreenImageData.initialIndex}
          />
        )}

        {/* Render Feedback Modal */}
        <FeedbackModal
          isOpen={showFeedbackForm}
          onClose={() => setShowFeedbackForm(false)}
          userEmail={user?.email || ""}
          triggerSource={feedbackTriggerSource}
          initialMessage={feedbackInitialMessage}
        />

        {/* Files modal */}
        {showFilesModal && (
          <div className="fixed inset-0 z-[88] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowFilesModal(false)} />
            <div className={`relative z-[89] w-[98%] sm:w-[80%] max-h-[95vh] rounded-2xl border p-5 overflow-y-auto shadow-2xl ${isDarkMode ? 'bg-[#1c1c1c] border-[#2a2a2a] text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Files</h3>
                <button className={`px-2 py-1 rounded-xl ${isDarkMode ? 'hover:bg-[#2a2a2a]' : 'hover:bg-gray-100'}`} onClick={() => setShowFilesModal(false)}>✕</button>
              </div>
              {filesModalPurpose === 'pick' && (
                <div className="mb-3">
                  <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Select up to {Math.max(0, 7 - (attachments?.length || 0))} images.
                  </div>
                </div>
              )}
              {(() => {
                const imgs = extractImagesFromMessages();
                const ai = imgs.filter(i => i.category === 'ai');
                const attached = imgs.filter(i => i.category === 'attached');
                const web = imgs.filter(i => i.category === 'web');
                const docs = extractDocsFromMessages();

                // Build a master list to preserve consistent indexing in fullscreen modal
                const ordered = [...ai, ...attached, ...web];
                const openCarousel = (globalIndex: number) => {
                  const all = ordered.map(i => ({ url: i.url }));
                  setFullscreenImageData({ images: all, initialIndex: globalIndex });
                  setShowFullscreenImageModal(true);
                };

                const openDocCarousel = (doc: { pageUrls: string[]; name: string }, initialIndex: number) => {
                  const all = (doc.pageUrls || []).map((url, idx) => ({ url, description: `Page ${idx + 1}` }));
                  if (all.length === 0) return;
                  setFullscreenImageData({ images: all, initialIndex: Math.max(0, Math.min(initialIndex, all.length - 1)) });
                  setShowFullscreenImageModal(true);
                };

                const section = (
                  title: string,
                  key: 'ai' | 'attached' | 'web',
                  list: typeof ordered,
                  offset: number
                ) => (
                  <div>
                    <button
                      className={`w-full flex items-center justify-between text-sm mb-2 px-2 py-2 rounded-lg ${isDarkMode ? 'hover:bg-[#2a2a2a]' : 'hover:bg-gray-100'}`}
                      onClick={() => setFilesCollapsed(prev => ({ ...prev, [key]: !prev[key] }))}
                    >
                      <span className="font-medium opacity-80">{title}</span>
                      <span className="flex items-center gap-1 opacity-60 text-xs">
                        <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${filesCollapsed[key] ? '' : 'rotate-90'}`} />
                        {list.length}
                      </span>
                    </button>
                    {!filesCollapsed[key] && (
                      list.length ? (
                        <div className="flex gap-2 flex-wrap justify-center">
                          {list.map((img, idx) => (
                            <button
                              key={`${key}-${idx}`}
                              className={`overflow-hidden rounded-xl border relative ${isDarkMode ? 'border-[#2a2a2a]' : 'border-gray-200'} ${filesModalPurpose === 'pick' && librarySelectedUrls.includes(img.url) ? (isDarkMode ? 'ring-2 ring-[#4EA3FF] border-[#4EA3FF]' : 'ring-2 ring-[#007AFF] border-[#007AFF]') : ''}`}
                              onClick={() => {
                                if (filesModalPurpose === 'pick') {
                                  toggleLibrarySelectedUrl(img.url);
                                } else {
                                  openCarousel(offset + idx);
                                }
                              }}
                              title={img.url}
                            >
                              <img src={getCloudinaryPreviewUrl(img.url, 90)} className="h-20 w-20 object-cover" />
                              {filesModalPurpose === 'pick' && librarySelectedUrls.includes(img.url) && (
                                <span className={`absolute top-0 right-0 rounded-full bg-transparent ${isDarkMode ? 'text-[#99CEFF]' : 'text-[#0285FF]'}`}>
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs opacity-60 px-2">No items</p>
                      )
                    )}
                  </div>
                );

                const docSection = (
                  title: string,
                  key: 'docs',
                  list: typeof docs,
                ) => (
                  <div>
                    <button
                      className={`w-full flex items-center justify-between text-sm mb-2 px-2 py-2 rounded-lg ${isDarkMode ? 'hover:bg-[#2a2a2a]' : 'hover:bg-gray-100'}`}
                      onClick={() => setFilesCollapsed(prev => ({ ...prev, [key]: !prev[key] }))}
                    >
                      <span className="font-medium opacity-80">{title}</span>
                      <span className="flex items-center gap-1 opacity-60 text-xs">
                        <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${filesCollapsed[key] ? '' : 'rotate-90'}`} />
                        {list.length}
                      </span>
                    </button>
                    {!filesCollapsed[key] && (
                      list.length ? (
                        <div className="flex gap-2 flex-wrap justify-center">
                          {list.map((doc, idx) => {
                            const cover = doc.pageUrls?.[0];
                            const ext = (() => {
                              const explicit = (doc.originalExt || '').toString().toLowerCase();
                              if (explicit) return explicit;
                              const n = String(doc.name || '').toLowerCase();
                              const e = n.includes('.') ? n.split('.').pop() : '';
                              return e || 'file';
                            })();
                            return (
                              <button
                                key={`docs-${idx}`}
                                type="button"
                                className={`relative overflow-hidden rounded-xl border w-[120px] h-[120px] ${isDarkMode ? 'border-[#2a2a2a] bg-[#141414]' : 'border-gray-200 bg-white'}`}
                                onClick={() => openDocCarousel(doc as any, 0)}
                                title={doc.name}
                              >
                                {cover && (
                                  <div
                                    className="absolute inset-0"
                                    style={{
                                      backgroundImage: `url(${cover})`,
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center',
                                    }}
                                  />
                                )}
                                <div className={`absolute inset-0 ${isDarkMode ? 'bg-black/55' : 'bg-white/55'}`} />
                                <div
                                  className="absolute inset-0 opacity-30"
                                  style={{
                                    backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, rgba(0,0,0,0.05) 2px, rgba(0,0,0,0.05) 3px)',
                                    mixBlendMode: 'overlay',
                                  }}
                                />
                                <div className="relative h-full w-full p-2 flex flex-col justify-between">
                                  <div className="flex flex-col gap-1 min-h-0">
                                    <div className={`text-[12px] break-words line-clamp-3 rounded-md border ${isDarkMode ? 'border-[#3a3a3a] bg-[#2a2a2a]/70 text-gray-200' : 'border-gray-200 bg-white/70 text-gray-700'}`}>
                                      {String(doc.name || 'Document')}
                                    </div>
                                    <div className={`text-[10px] opacity-80 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                      {doc.pageUrls?.length ? `${doc.pageUrls.length} pages` : 'Document'}
                                    </div>
                                  </div>
                                  <div className="flex justify-end">
                                    <div className={`h-[18px] flex flex-row items-center justify-center px-1 border shadow-sm rounded font-medium ${isDarkMode ? 'border-[#3a3a3a] bg-[#2a2a2a]/70 text-gray-200' : 'border-gray-200 bg-white/70 text-gray-700'}`}>
                                      <p className="uppercase truncate text-[11px] leading-[13px]">{ext}</p>
                                    </div>
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-xs opacity-60 px-2">No items</p>
                      )
                    )}
                  </div>
                );

                const aiOffset = 0;
                const attachedOffset = aiOffset + ai.length;
                const webOffset = attachedOffset + attached.length;

                return (
                  <div className="space-y-4">
                    {section('Created Images', 'ai', ai, aiOffset)}
                    {section('Attached Images', 'attached', attached, attachedOffset)}
                    {docSection('Documents', 'docs', docs)}
                    {filesModalPurpose === 'pick' && (
                      <div className="pt-2 flex items-center justify-end gap-2">
                        <button
                          type="button"
                          className={`px-3 py-2 rounded-full ${isDarkMode ? 'bg-transparent hover:bg-[#2a2a2a] text-gray-200' : 'bg-transparent hover:bg-gray-100 text-gray-700'}`}
                          onClick={() => {
                            setLibrarySelectedUrls([]);
                            setShowFilesModal(false);
                            setFilesModalPurpose('view');
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className={`px-4 py-2 rounded-full ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'} ${librarySelectedUrls.length === 0 ? 'opacity-70 cursor-not-allowed' : ''}`}
                          onClick={confirmAttachLibrarySelected}
                          disabled={librarySelectedUrls.length === 0}
                        >
                          Attach ({librarySelectedUrls.length})
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Rename Chat Modal */}
        {showRenameModal && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowRenameModal(false)} />
            <div className={`relative z-[81] w-[92%] max-w-md rounded-2xl border p-5 shadow-2xl ${isDarkMode ? 'bg-[#1c1c1c] border-[#2a2a2a] text-white' : 'bg-white border-gray-200 text-gray-900'
              }`}>
              <h3 className="text-lg font-semibold mb-2">Rename Chat</h3>
              <input
                type="text"
                value={newChatName}
                onChange={(e) => setNewChatName(e.target.value)}
                placeholder="Enter new chat name"
                className={`w-full px-3 py-2 rounded-lg border mb-4 ${isDarkMode
                  ? 'bg-[#2a2a2a] border-[#3a3a3a] text-white placeholder-gray-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleRenameChat();
                  }
                }}
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <button
                  className={`px-3 py-2 rounded-full ${isDarkMode ? 'bg-transparent hover:bg-[#2a2a2a] text-gray-300' : 'bg-transparent hover:bg-gray-100 text-gray-700'}`}
                  onClick={() => setShowRenameModal(false)}
                >
                  Cancel
                </button>
                <button
                  className={`px-4 py-2 rounded-full ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                  onClick={handleRenameChat}
                  disabled={!newChatName.trim()}
                >
                  Rename
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => !isDeleting && setShowDeleteModal(false)} />
            <div className={`relative z-[81] w-[92%] max-w-md rounded-2xl border p-5 shadow-2xl ${isDarkMode ? 'bg-[#1c1c1c] border-[#2a2a2a] text-white' : 'bg-white border-gray-200 text-gray-900'
              }`}>
              <h3 className="text-lg font-semibold mb-2">Delete Chat</h3>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-4`}>
                Are you sure you want to delete "{currentChatTitle || 'this chat'}"? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  className={`px-3 py-2 rounded-full ${isDarkMode ? 'bg-transparent hover:bg-[#2a2a2a] text-gray-300' : 'bg-transparent hover:bg-gray-100 text-gray-700'}`}
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  className={`px-4 py-2 rounded-full flex items-center gap-2 ${isDarkMode ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'} ${isDeleting ? 'opacity-70 cursor-not-allowed' : ''}`}
                  onClick={handleDeleteChat}
                  disabled={isDeleting}
                >
                  {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isDeleting ? 'Deleting...' : 'Yes, delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Incognito Reload Confirmation Modal */}
        {showIncognitoReloadConfirm && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => !isReloadingIncognito && setShowIncognitoReloadConfirm(false)}
            />
            <div
              className={`relative z-[81] w-[92%] max-w-md rounded-2xl border p-5 shadow-2xl ${isDarkMode
                ? 'bg-[#1c1c1c] border-[#2a2a2a] text-white'
                : 'bg-white border-gray-200 text-gray-900'
                }`}
            >
              <h3 className="text-lg font-semibold mb-2">Reload Page</h3>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-4`}>
                You're in Incognito Mode. Reloading will clear all temporary chats. Do you want to proceed?
              </p>
              <div className="flex justify-end gap-2">
                <button
                  className={`${isDarkMode
                    ? 'bg-transparent hover:bg-[#2a2a2a] text-gray-300'
                    : 'bg-transparent hover:bg-gray-100 text-gray-700'
                    } px-3 py-2 rounded-full disabled:opacity-60`}
                  onClick={() => !isReloadingIncognito && setShowIncognitoReloadConfirm(false)}
                  disabled={isReloadingIncognito}
                >
                  Cancel
                </button>
                <button
                  className={`${isDarkMode
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                    } px-4 py-2 rounded-full disabled:opacity-70`}
                  onClick={() => {
                    if (!isReloadingIncognito) confirmIncognitoReload();
                  }}
                  disabled={isReloadingIncognito}
                  aria-busy={isReloadingIncognito}
                >
                  {isReloadingIncognito ? 'Reloading…' : 'Reload'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Share Dialog */}
        {showShareDialog && currentChatId && (
          <ShareDialog
            isOpen={showShareDialog}
            onClose={() => setShowShareDialog(false)}
            chatId={currentChatId}
            chatTitle={currentChatTitle || "Untitled Chat"}
            isDarkMode={isDarkMode}
          />
        )}

        {/* Render Incognito Mode Modal */}
        <IncognitoModeModal
          isOpen={showIncognitoModal}
          onClose={() => setShowIncognitoModal(false)}
          isDarkMode={isDarkMode}
        />


        {/* Global selection actions tooltip (AI-only selection) */}
        <SelectionActionsTooltip
          isDarkMode={isDarkMode}
          selectedModel={selectedModel}
          selectedAgentName={selectedAgent?.name || null}
        />
      </div >
    </>
  );
};

export default Index;

export type { ImageModalData }
